import type { Dict } from "./en";

const fr: Dict = {
  grid: {
    controls: { group: "Commandes de zoom de la grille", zoomIn: "Zoom avant", zoomOut: "Zoom arrière", fit: "Ajuster la grille à la vue" },
    hud: { aria: "HUD de coordonnées de la grille", instrument: "Instrument", xy: "X · Y", zoom: "Zoom", grid: "Grille", chain: "Réseau" },
    legend: {
      gridZero: { label: "Grid Zero", desc: "Noyau central verrouillé" },
      vipCore: { label: "VIP Core", desc: "Anneau central de prestige · vendable" },
      premium: { label: "Premium", desc: "Boulevards et anneau intérieur" },
      reserve: { label: "Réserve", desc: "4 zones réservées verrouillées" },
      standard: { label: "Standard", desc: "Territoire général" },
    },
    loading: { initializing: "Initialisation du territoire" },
    mode: { aria: "Mode d'interaction de la grille", select: "Sélectionner", selectHint: "Tracer un rectangle", inspect: "Inspecter", inspectHint: "Cliquer les parcelles", itemAria: "Mode {label} — {hint}" },
    enter3d: { title: "Entrer en 3D", fallbackName: "territoire" },
    panel: {
      parcelProfileAria: "Profil de parcelle", parcelProfileTitle: "Profil de parcelle", parcel: "Parcelle",
      territoryName: "Nom du territoire", owner: "Propriétaire", position: "Position", size: "Taille", slots: "Emplacements", zone: "Zone",
      claimBlock: "Bloc de claim", claimTx: "Tx de claim", selectTerritory: "Sélectionner un territoire",
      emptySelection: "La cellule n'a pas de parcelle. Cliquez un territoire possédé pour voir son dossier.",
      emptyDefault: "Cliquez une parcelle sur la grille pour voir la propriété, les coordonnées et l'identité on-chain.",
      contextAria: "Panneau de contexte", panelAria: "Panneau", closePanel: "Fermer le panneau", snapshot: "Snapshot",
      titleArtwork: "Œuvre", titleSelection: "Sélection", titleContext: "Contexte",
      parcelNum: "Parcelle n°{id}", selectionSize: "{w} × {h} · {slots} emplacements", drawRectHint: "Tracez un rectangle sur la grille",
    },
    context: {
      emptyTitle: "Aucun territoire sélectionné",
      emptyDesc: "Tracez un rectangle sur la grille pour prévisualiser emplacements, zone, niveau de rareté et prix.",
    },
    detail: {
      fetchFailed: "Échec de récupération des détails : {error}",
      conflict: "Conflit d'invariant de données sur cette cellule : {count} parcelles se chevauchent. Sélection supprimée.",
      cellOutside: "Hors de la grille ({x}, {y})",
      cellLoadingMap: "Chargement des données de carte…",
      cellConfirming: "Confirmation de la cellule ({x}, {y})…",
      cellEmpty: "Cellule ({x}, {y}) : aucune parcelle indexée sur cette cellule (au bloc {block})",
      cellConfirmError: "Impossible de confirmer l'état actuel de la cellule.",
      chainMismatch: "Viewer indisponible en raison d'une discordance de réseau.",
      indexerNotReady: "Indexeur pas prêt — aucun snapshot pour l'instant.",
      clickHint: "Cliquez une parcelle pour voir les détails.",
      zoneClassifying: "Classification…",
      zoneUnavailable: "Indisponible",
      zoneSaleLocked: "· vente verrouillée",
      zoneMixed: "· zone mixte",
    },
    toolbar: {
      territory: "Territoire",
      scrollToZoom: "défiler pour zoomer",
      menu: "Menu",
      openNav: "Ouvrir la navigation",
      hideNav: "Masquer la navigation",
      revealAreas: "Afficher les zones",
      revealAreasAria: "Afficher les zones du territoire",
      zonesUnavailable: "Carte des zones indisponible — nouvelle tentative automatique",
      zonesRetry: "Réessayer",
      showDragArtwork: "Afficher et déplacer l'œuvre",
      showDragArtworkAria: "Afficher et déplacer l'œuvre",
      showDragArtworkTitle: "Afficher mon œuvre sur la grille et la déplacer vers une autre parcelle",
      loading: "Chargement…",
      myTerritories: "Mes territoires",
    },
    status: {
      live: "DIRECT", cached: "CACHE", chain: "RÉSEAU",
      liveTitle: "En direct · synchronisé",
      cachedTitle: "Affichage du dernier snapshot connu",
      chainTitle: "Discordance de réseau — données du viewer supprimées",
    },
    artworkMsg: {
      none: "Vous n'avez pas encore de Frozen Artworks.",
      noPlaced: "Aucune œuvre placée à déplacer.",
      connectWallet: "Connectez votre wallet pour déplacer vos œuvres.",
    },
    slotCount: {
      slots: "emplacements",
      selectedTitle: "{slots} emplacements sélectionnés ({w} × {h})",
    },
    bottomSheet: { aria: "Panneau inférieur", dragOpen: "Glisser pour redimensionner ou replier", handle: "Poignée du panneau" },
    stats: {
      networkStatus: "État du réseau", live: "En direct", offline: "Hors ligne", syncing: "Synchronisation", chain: "Réseau",
      parcelsClaimed: "Parcelles revendiquées", onChain: "on-chain", cellsAllocated: "Cellules allouées",
      finiteWorld: "{pct}% du monde fini", indexerHead: "Tête de l'indexeur", finalized: "finalisé #{block}",
    },
    capacity: {
      territory: "Territoire", resolving: "Résolution du territoire…", unavailable: "Capacité du territoire indisponible",
      area: "Surface du territoire", publishability: "Publiabilité", publishable: "Publiable",
      notPublishable: "Non publiable · Minimum {min} u²", gpCapacity: "Capacité GP", propertyUnits: "Unités de propriété",
    },
    artworks: {
      title: "Œuvres sur cette terre", scanning: "Analyse…",
      empty: "Aucune Frozen Artwork n'est encore placée sur cette terre.", untitled: "Œuvre sans titre", view: "Voir →",
    },
    zoomHint: {
      copy: "Zoomez pour explorer The Grid. Au zoom maximal, découvrez ce que les créateurs ont publié sur chaque parcelle.",
      controlDesktop: "Molette ou trackpad pour zoomer",
      controlMobile: "Pincez pour zoomer",
      dontShow: "Ne plus afficher",
      close: "Fermer",
      enteringParcels: "Vous entrez dans les parcelles",
    },
  },
  nav: {
    marketplace: "Marketplace",
    feed: "Fil",
    rent: "RENT",
    overview: "Aperçu",
    about: "Le Projet",
    howItWorks: "Comment ça marche",
    explore: "Explorer",
    creators: "Créateurs et Marques",
    creatorsShort: "Créateurs",
    territory: "Territoire",
    profile: "Profil",
    enterGrid: "Entrer dans la Grid",
    signIn: "Se connecter",
    navigate: "Naviguer",
    purchaseBadge: "Achat",
    openMenuAria: "Ouvrir le menu de navigation",
    closeMenuAria: "Fermer le menu de navigation",
    primaryAria: "Principal",
    howItWorksHintAria: "Nouveau ici ? Découvrez comment fonctionne THE GRID",
  },
  lang: { ariaLabel: "Langue", menuLabel: "Choisir la langue" },
  wallet: {
    connect: "Connecter",
    connecting: "Connexion…",
    myTerritories: "Mes territoires",
    sessionDetails: "Détails de session",
    disconnect: "Déconnecter",
  },
  auth: {
    title: "Session",
    subtitle: "Base d'authentification par wallet · SIWE + EIP-1271/6492",
    walletLabel: "Wallet",
    sessionLabel: "Session",
    connectPrompt: "Connectez un wallet via la navigation en haut pour activer la connexion.",
    loading: "Chargement…",
    signedInAs: "Connecté en tant que {wallet}",
    expires: "expire : {date}",
    signOut: "Se déconnecter",
    signIn: "Se connecter avec Ethereum",
    chain: "réseau : {name} ({id})",
    status: {
      requestingNonce: "Demande du nonce…",
      requestingSignature: "Demande de signature au wallet…",
      verifying: "Vérification de la signature…",
      signedIn: "Connecté en tant que {wallet} (chemin : {path})",
      signedOut: "Déconnecté",
    },
  },
  home: {
    hero: {
      eyebrow: "// UN MONDE. DES POSSIBILITÉS FINIES.",
      paragraph:
        "Un territoire unique et fini d'un million de cellules. Explorez, revendiquez et possédez on-chain. Chaque parcelle est frappée depuis le même registre, attribuée une seule fois, pour toujours.",
      enterGrid: "Entrer dans la Grid",
      scrollLine1: "Faites défiler pour",
      scrollLine2: "Explorer",
      scrollAria: "Faites défiler pour explorer",
    },
    instrument: {
      title: "Instrument",
      labels: { grid: "GRILLE", chain: "RÉSEAU", maxZoom: "MAX·Z", auth: "AUTH" },
    },
    callouts: {
      gridZero: { title: "Grid Zero", body: "Noyau du territoire" },
      lattice: { title: "Treillis de données", body: "Limites de zone on-chain" },
      cells: { label: "Cellules · Finies" },
    },
    features: [
      { title: "Fini par Conception", body: "1 000 000 de cellules. Jamais plus." },
      { title: "Construit sur Base", body: "Territoire on-chain · aujourd'hui sur Base Sepolia." },
      { title: "Explorable par Tous", body: "Lisez la carte sans wallet." },
      { title: "Propriété du Wallet", body: "SIWE · EIP-1271 · EIP-6492 vérifiés." },
    ],
    story: {
      eyebrow: "Notre Histoire",
      titleLine1: "UNE NOUVELLE ÈRE",
      titleLine2: "DE LA",
      titleHighlight: "PROPRIÉTÉ",
      paragraph:
        "La Grid est née d'une idée simple : un territoire numérique fini par construction, dont les limites vivent dans le registre — pas sur la page marketing. Ce que vous possédez ici, la chaîne s'en souvient.",
    },
    roadmap: {
      phaseLabel: "Phase",
      eyebrow: "Chronologie",
      phasesLive: "{live} phases sur {total} actives",
      title: "CE QUI EST LIVRÉ. CE QUI ARRIVE.",
      paragraph:
        "Une carte honnête, lue directement depuis Base. Fondations et Propriété sont actives aujourd'hui — revendiquez, créez, publiez et faites-vous découvrir dès maintenant. Nous sommes dans la phase de finition et de corrections finales ; le prochain grand jalon est le Mainnet, et il apparaîtra ici seulement quand ce sera le registre, et non le texte, qui le rendra réel.",
      status: { live: "Actif", next: "En cours", later: "Prévu" },
      phases: [
        {
          title: "Fondations",
          items: [
            "Territoire canonique de 1 000 000 de cellules sur Base",
            "Indexer + reconciler on-chain actifs",
            "Authentification wallet SIWE · EIP-1271 · EIP-6492",
            "Exploration publique, sans wallet",
          ],
        },
        {
          title: "Propriété et Création",
          items: [
            "Revendication de parcelles et règlement en USDC sur Base",
            "Canvas créateur — liens, réseaux, médias, modèles",
            "Couche voxel 3D optionnelle",
            "Publication + découverte honnête sur Explore",
          ],
        },
        {
          title: "Finition finale",
          items: [
            "Corrections de recette & finition UX",
            "Couverture multilingue complète",
            "Durcissement & QA finale avant le lancement",
            "Page d'aperçu Parcel RENT · sortie prévue Q1 2027",
          ],
        },
        {
          title: "Mainnet",
          items: [
            "Déploiement sur le Mainnet (Base)",
            "Offres, marché secondaire et activité",
            "Certificats et vérification des créateurs",
            "API publiques et extensions",
          ],
        },
      ],
    },
    cta: {
      eyebrow: "Entrer",
      titlePrefix: "CHOISISSEZ VOTRE",
      titleHighlight: "TERRITOIRE",
      paragraph:
        "La carte que vous avez explorée est publique. La propriété ne l'est pas. Connectez-vous avec votre wallet pour prendre votre position — le registre s'en souviendra.",
      exploreGrid: "Explorer la Grid",
      signIn: "Se connecter avec Ethereum",
    },
    footer: {
      legalAria: "Mentions légales",
      legalToggle: "Mentions légales et politiques",
      walletAuth: "Authentifié par wallet",
      territoryData: "Données du territoire · lues on-chain",
    },
    slidedots: {
      aria: "Navigation des sections",
      overview: "Aperçu",
      story: "Histoire",
      territory: "Territoire",
      roadmap: "Roadmap",
      enter: "Entrer",
    },
  },
  about: {
    eyebrow: "Le Manifeste",
    titleLine1: "UN MONDE",
    titleHighlight: "FINI.",
    intro:
      "THE GRID est un territoire numérique unique et fini d'un million de cellules, attribué une seule fois et possédé on-chain. Voilà ce en quoi nous croyons et ce que le produit fait vraiment — rien de plus, rien d'inventé.",
    principles: [
      {
        title: "Fini par construction",
        body: "Un territoire. Un million de cellules. Jamais plus. Ici la rareté n'est pas un choix marketing — elle est inscrite dans le contrat et ne peut être gonflée.",
      },
      {
        title: "La propriété vit on-chain",
        body: "Une parcelle est un actif on-chain sur Base. Ce que vous détenez est enregistré par le registre, pas par nous. Les limites de votre territoire sont vérifiables, pas promises.",
      },
      {
        title: "Public par défaut",
        body: "N'importe qui peut lire la carte — sans wallet, sans inscription. La découverte est ouverte. La propriété est la seule chose qui exige de prouver qui vous êtes.",
      },
      {
        title: "Une toile, pas un panneau",
        body: "Chaque territoire est une vraie surface de design : texte, liens, réseaux, médias, formes et une couche 3D optionnelle, le tout dans un budget honnête de Grid-Power dérivé de votre terrain.",
      },
      {
        title: "Votre identité, vérifiée",
        body: "Sign-In With Ethereum avec des wallets EOA et smart-contract (EIP-1271 / EIP-6492). Nous ne gardons jamais vos clés et ne déplaçons jamais vos fonds.",
      },
      {
        title: "Fait pour durer",
        body: "Le contenu publié est un instantané durable, séparé de votre brouillon de travail. Éditer plus tard ne casse jamais ce que le public voit déjà, jusqu'à ce que vous republiiez.",
      },
    ],
  },
  how: {
    eyebrow: "Le Parcours",
    titleLine1: "DE LA CARTE",
    titleLine2: "À LA PROPRIÉTÉ.",
    intro:
      "Six étapes réelles, chacune adossée à une fonctionnalité déjà livrée. Voici toute la boucle — explorer, revendiquer, concevoir, publier et se faire découvrir.",
    stepLabel: "ÉTAPE {n}",
    toggle: { show: "Comment ça marche", hide: "Masquer", aria: "Afficher une explication simple étape par étape" },
    steps: [
      {
        title: "Explorez la carte",
        body: "Ouvrez le Territoire et parcourez la Grid finie de 1000×1000. Zoomez sur n'importe quelle parcelle pour voir qui possède quoi — sans wallet.",
        detail: {
          intro: "Regarder autour de soi est totalement gratuit et public — sans wallet, sans compte.",
          points: [
            "Ouvrez Territoire depuis le menu du haut pour voir toute la carte : une seule grille de 1 000 000 de petits carrés (cellules).",
            "Glissez pour vous déplacer, faites défiler pour zoomer. Zoomez sur une zone pour voir quelle terre est prise et ce que les propriétaires y ont placé.",
            "Ici rien ne vous demande de vous connecter — lire la carte est toujours ouvert à tous.",
          ],
        },
      },
      {
        title: "Revendiquez votre parcelle",
        body: "Dessinez un rectangle sur une terre libre et revendiquez-la sur Base. La propriété est frappée on-chain — votre position est enregistrée par le registre, pas par nous.",
        detail: {
          intro: "« Revendiquer » signifie devenir propriétaire d'un rectangle de cellules libres. La propriété est inscrite sur Base, une blockchain publique.",
          points: [
            "Il vous faudra un wallet crypto — une petite application gratuite du navigateur qui prouve que c'est vous et approuve les actions.",
            "Aujourd'hui THE GRID tourne sur Base Sepolia, un réseau de test, donc vous utilisez de l'USDC de test — pas de l'argent réel.",
            "Sur la carte, dessinez un rectangle sur une terre libre et confirmez. Votre wallet signe et la parcelle vous est frappée.",
          ],
        },
      },
      {
        title: "Concevez votre Canvas",
        body: "Transformez votre territoire en une vraie page : titres, liens, réseaux, contacts, médias et formes — ou partez de l'un des six modèles premium. Tout tient dans un budget honnête de Grid-Power dérivé de la surface de votre terrain.",
        detail: {
          intro: "Votre parcelle est une page blanche que vous contrôlez. Remplissez-la, ou partez d'un modèle prêt à l'emploi.",
          points: [
            "Ajoutez des titres, des liens, des boutons sociaux, des contacts, des images, de la vidéo et des formes simples.",
            "Vous préférez un coup de pouce ? Chargez l'un des six modèles premium et ajustez chaque détail.",
            "Chaque parcelle a un budget de Grid-Power selon sa taille — plus de terre vous laisse placer davantage. Cela garde les pages honnêtes et rapides.",
          ],
        },
      },
      {
        title: "Ajoutez une couche 3D (optionnel)",
        body: "Donnez de la profondeur à votre territoire avec une scène voxel où les visiteurs peuvent entrer en zoomant. Totalement optionnel — un Canvas plat est aussi un résultat de premier plan.",
        detail: {
          intro: "Par-dessus la page plate, vous pouvez ajouter une petite scène 3D faite de blocs (voxels).",
          points: [
            "Les visiteurs entrent dans la scène en zoomant en profondeur sur votre parcelle.",
            "C'est totalement optionnel — vous n'êtes jamais obligé d'en créer une.",
            "Un Canvas plat sans 3D est un résultat complet et de premier plan.",
          ],
        },
      },
      {
        title: "Publiez",
        body: "Publier prend un instantané durable de votre brouillon enregistré. Continuez à éditer en privé — le public voit exactement ce que vous avez publié jusqu'à ce que vous republiiez.",
        detail: {
          intro: "Publier crée une copie publique de votre travail — tandis que votre brouillon privé reste le vôtre à éditer.",
          points: [
            "Cela prend un instantané durable de votre brouillon enregistré et le montre au monde.",
            "Vous pouvez continuer à éditer en privé ; les visiteurs continuent de voir la version publiée.",
            "Rien de ce que vous changez ne devient public tant que vous ne republiez pas.",
          ],
        },
      },
      {
        title: "Faites-vous découvrir",
        body: "Chaque publication valide et actuelle apparaît sur Explore, la plus récente d'abord — sans pay-to-rank, sans faux trending. Propriété réelle, contenu réel, ordre honnête.",
        detail: {
          intro: "Une fois publié, votre territoire est référencé pour tous — automatiquement et équitablement.",
          points: [
            "Il apparaît sur la page Explore, les plus récents d'abord.",
            "On ne paie pas pour se classer plus haut et il n'y a pas de faux « trending ».",
            "Propriété réelle, contenu réel, ordre honnête.",
          ],
        },
      },
    ],
    before: {
      eyebrow: "Commencez ici",
      title: "Avant de commencer",
      intro: "Pour regarder autour de vous, il ne faut presque rien. Vous n'avez besoin d'un wallet que lorsque vous décidez de posséder de la terre.",
      items: [
        { title: "Pour explorer : rien", body: "Lire la carte et ouvrir n'importe quelle page publiée est totalement public — sans wallet, sans inscription." },
        { title: "Un wallet crypto", body: "Pour revendiquer et posséder, il vous faut un wallet : une petite application gratuite du navigateur qui prouve que c'est vous. Nous ne gardons jamais vos clés ni vos fonds." },
        { title: "Des fonds de test, pas de l'argent réel", body: "Aujourd'hui THE GRID tourne sur Base Sepolia, un réseau de test gratuit. Vous utilisez de l'USDC de test pour tout essayer en toute sécurité." },
        { title: "Quelques minutes", body: "Revendiquer, concevoir et publier prennent des minutes — pas des heures." },
      ],
    },
    journey: {
      eyebrow: "De bout en bout",
      title: "Le parcours complet",
      intro: "Toute la boucle d'un coup d'œil. Chaque étape mène naturellement à la suivante.",
      optional: "optionnel",
      steps: ["Explorer", "Revendiquer", "Concevoir", "Couche 3D", "Publier", "Être découvert"],
    },
    draft: {
      eyebrow: "Deux versions",
      title: "Brouillon vs Publié",
      intro: "Votre travail vit toujours à deux endroits. Comprendre la différence, c'est toute l'astuce.",
      draftLabel: "Brouillon",
      draftBody: "Votre copie de travail privée. Vous seul la voyez. Éditez autant que vous voulez, quand vous voulez.",
      publishedLabel: "Publié",
      publishedBody: "Un instantané public durable. C'est ce que voient les visiteurs. Il ne change jamais tant que vous ne republiez pas.",
      takeaway: "Éditer votre brouillon est toujours sans risque — le public continue de voir votre dernière version publiée.",
    },
    levels: {
      eyebrow: "Trois niveaux de zoom",
      title: "Comment THE GRID se vit",
      intro: "Le même territoire révèle davantage à mesure que vous vous approchez.",
      items: [
        { tag: "Dézoomé", title: "La carte", body: "Voyez tout le territoire fini d'un coup et où se trouve chaque chose." },
        { tag: "Zoomé", title: "Une page", body: "Zoomez sur une parcelle et elle devient une vraie page — le Canvas du propriétaire avec texte, liens et médias." },
        { tag: "Plus profond (optionnel)", title: "En 3D", body: "Continuez à zoomer sur une parcelle dotée d'une couche 3D et vous entrez dans sa scène voxel." },
      ],
    },
    faq: {
      eyebrow: "Questions de débutant",
      title: "FAQ débutant",
      intro: "Nouveau sur THE GRID ou dans le Web3 ? Commencez ici.",
      items: [
        { q: "Ai-je besoin d'un wallet juste pour regarder ?", a: "Non. Explorer la carte et ouvrir n'importe quelle page publiée est totalement public et gratuit." },
        { q: "Qu'est-ce qu'un « wallet » ?", a: "Une petite application — souvent une extension de navigateur — qui détient votre identité on-chain et vous laisse approuver des actions. Vous gardez le contrôle ; nous ne gardons jamais vos clés ni vos fonds." },
        { q: "Est-ce de l'argent réel ?", a: "Pas aujourd'hui. THE GRID tourne sur Base Sepolia, un réseau de test gratuit, avec de l'USDC de test — pour tout essayer en toute sécurité." },
        { q: "Que sont les cellules et les parcelles ?", a: "THE GRID est une seule carte de 1 000 000 de petits carrés appelés cellules. Une parcelle est un rectangle de cellules que vous possédez." },
        { q: "Puis-je modifier ma page après publication ?", a: "Oui. Vous gardez un brouillon privé et pouvez l'éditer à tout moment. Le public continue de voir votre dernière version publiée jusqu'à ce que vous republiiez." },
        { q: "Qu'est-ce que le Grid-Power ?", a: "Un budget simple basé sur la taille de votre terrain qui limite ce que vous pouvez placer, gardant les pages honnêtes et rapides à charger." },
      ],
    },
  },
  explore: {
    eyebrow: "Découvrir",
    titleLine1: "TERRITOIRES",
    titleHighlight: "PUBLIÉS.",
    intro:
      "Des territoires réels et actuellement en ligne sur THE GRID — les plus récents d'abord. Sans pay-to-rank, sans trending inventé, sans faux compteurs. Ouvrez-en un pour voir sa page publique.",
    error: {
      title: "Impossible de charger l'annuaire public",
      body: "Le service de découverte est indisponible pour le moment. Réessayez dans un instant.",
      retry: "Réessayer",
    },
    empty: {
      title: "La carte attend",
      body: "Aucun territoire n'a encore été publié. Quand les créateurs publieront leur Canvas, ils apparaîtront ici — les plus récents d'abord. Vous pourriez être le premier.",
      claim: "Revendiquez votre territoire",
      how: "Comment ça marche",
    },
    liveOne: "{count} territoire actif",
    liveOther: "{count} territoires actifs",
    modulesOne: "{count} module",
    modulesOther: "{count} modules",
    untitled: "Territoire sans titre",
    view: "Voir le territoire",
    badgeMedia: "Médias",
  },
  creators: {
    eyebrow: "Créateurs et Marques",
    titleLine1: "VOTRE TERRITOIRE,",
    titleHighlight: "VOTRE SCÈNE.",
    intro:
      "Quoi que vous fassiez, il y a un point de départ pour cela. Six modèles Canvas premium transforment un territoire possédé en une page finie en quelques minutes — puis vous éditez chaque pixel. Les modèles se chargent dans votre propre brouillon dans l'éditeur ; rien n'est publié tant que vous ne le décidez pas.",
    sixTemplates: "Six modèles premium",
    useCases: {
      creator: "Pour les créateurs qui bâtissent une marque personnelle — une identité forte, une bio et un seul appel à l'action.",
      retail: "Pour les petites boutiques et marques — un en-tête au-dessus d'un rayon de produits avec un chemin d'achat clair.",
      gallery: "Pour les artistes — l'œuvre mène, une déclaration discrète suit.",
      music: "Pour les sorties et événements live — titre, date, lieu et un lien direct billets / stream.",
      product: "Pour les lancements — titre, ligne de soutien, trois blocs de fonctionnalités et une CTA.",
      minimal: "Pour le minimalisme haut de gamme — retenue, espace blanc et une seule action réfléchie.",
    },
    noteBefore: "Les modèles vivent dans l'éditeur Canvas pour un territoire que vous possédez. Ouvrez un territoire, appuyez sur ",
    noteBold: "Modèles",
    noteAfter: ", choisissez un point de départ — il remplit votre brouillon, et Enregistrer / Publier restent entre vos mains.",
    howCta: "Comment ça marche",
  },
  rent: {
    eyebrow: "Fonctionnalité prévue",
    titleLine1: "PARCEL",
    titleHighlight: "RENT",
    intro: "Un moyen prévu pour permettre aux propriétaires de territoires de louer seulement une partie de leur territoire à des marques pour des campagnes promotionnelles temporaires — tout en conservant la pleine propriété de leur parcelle.",
    release: "Sortie prévue : Q1 2027",
    status: "Pas encore disponible · Aperçu informatif",
    coexist: {
      eyebrow: "Territoire partagé",
      title: "Un territoire. Deux zones distinctes.",
      body: "Le propriétaire conserve son propre espace et son identité. La campagne d'un sponsor n'existerait que dans la zone louée, pour une durée limitée — les deux coexistent côte à côte, sans jamais se mélanger.",
      ownerLabel: "Propriétaire",
      sponsorLabel: "Sponsor",
      ownerLegend: "Zone du propriétaire — reste au propriétaire",
      sponsorLegend: "Zone du sponsor — louée, à durée limitée",
      caption: "Exemple illustratif — ni un vrai territoire ni une vraie campagne",
    },
    how: {
      eyebrow: "Comment cela devrait fonctionner",
      steps: [
        { title: "La propriété reste", body: "La parcelle et son NFT ne changent jamais de mains pendant une location." },
        { title: "Seule une partie est louée", body: "Le propriétaire choisit la zone à proposer ; le reste du territoire reste intact." },
        { title: "Campagnes temporaires", body: "Un sponsor ne pourrait publier que dans la zone approuvée, et l'accès prend fin à l'expiration de la location." },
        { title: "Vérifié et officiel", body: "Des vérifications d'identité prévues pour les participants RENT et des paiements via le parcours de location officiel de The Grid." },
      ],
    },
    model: {
      eyebrow: "Modèle économique proposé",
      title: "Des revenus locatifs pour les propriétaires. Une commission pour The Grid.",
      body: "Selon le modèle proposé, le propriétaire du territoire pourrait percevoir des revenus locatifs sur la zone louée, tandis que The Grid percevrait une commission sur chaque transaction de location officielle.",
      cards: [
        { title: "Propriétaires", body: "Des revenus locatifs potentiels sur une partie du territoire, sans vendre la parcelle." },
        { title: "Marques", body: "Une présence promotionnelle temporaire dans des territoires de créateurs sélectionnés." },
        { title: "The Grid", body: "Une commission sur les transactions de location officielles." },
      ],
      note: "Les niveaux de commission, les prix et les conditions de location n'ont pas encore été définis.",
    },
    disclaimer: "Parcel RENT est une fonctionnalité prévue et n'est pas disponible actuellement. Aucune réservation, aucun paiement ni aucune location n'est possible aujourd'hui. Les détails, l'éligibilité, les prix et le calendrier de lancement peuvent changer ; aucun gain, trafic ou résultat n'est garanti.",
  },
  shellCta: {
    title: "La carte est publique. La propriété ne l'est pas.",
    body: "Explorez librement chaque territoire publié. Quand vous êtes prêt à revendiquer une position, connectez-vous avec votre wallet — le registre s'en souviendra.",
    explore: "Explorer les territoires",
    enterGrid: "Entrer dans la Grid",
  },
  onboarding: {
    profileEntry: { eyebrow: "Parcours guidé" },
    eyebrow: "Parcours guidé",
    title: "Lancez votre territoire",
    intro: "Un parcours guidé de zéro à un territoire publié. Rien n'est imposé — quittez quand vous voulez et reprenez où vous en étiez. Chaque étape vous mène à l'outil réel, et votre progression est enregistrée.",
    authRequired: {
      title: "Connectez-vous pour démarrer le guide",
      body: "Le parcours guidé est lié à votre wallet pour enregistrer votre progression. Connecter un wallet ne suffit pas — une session signée est requise.",
      cta: "Se connecter avec Ethereum",
    },
    loading: "Chargement de votre guide…",
    error: {
      title: "Impossible de charger votre guide",
      body: "Le service de guide est indisponible pour le moment. Réessayez bientôt.",
      retry: "Réessayer",
    },
    progress: {
      label: "Étape {n} sur {total}",
      complete: "{done} sur {total} faits",
      statusDone: "Fait",
      statusNow: "Maintenant",
      statusNext: "Suivant",
      statusLocked: "Plus tard",
    },
    restart: "Recommencer le guide",
    restartConfirm: "Recommencer le guide ? Cela réinitialise uniquement votre progression guidée — vos territoires, brouillons et publications ne sont jamais touchés.",
    ownershipLost: "Le territoire que vous aviez sélectionné n'appartient plus à ce wallet. Nous vous avons ramené à la sélection du territoire.",
    actions: {
      select: "Sélectionner",
    },
    personas: {
      selected: "Sélectionné",
      creator: {
        label: "Créateur",
        desc: "Une marque personnelle, un portfolio ou une identité unique et forte avec un appel à l'action clair.",
      },
      retail: {
        label: "Entreprise / Commerce",
        desc: "Une boutique, un produit ou une marque — un en-tête clair sur ce que vous offrez et un chemin pour agir.",
      },
    },
    steps: {
      persona: {
        title: "Qui êtes-vous ?",
        body: "Dites-nous si vous êtes un Créateur ou une Entreprise. Nous adaptons les points de départ — vous pouvez changer à tout moment.",
      },
      territory: {
        title: "Obtenez votre territoire",
        body: "Possédez une partie de la Grid, puis choisissez le territoire que ce guide va construire.",
        pick: "Choisissez le territoire à construire",
        selectedLabel: "En construction",
        needLand: "Vous ne possédez pas encore de terrain. Réclamez un parcel sur la Grid pour commencer.",
        getLand: "Obtenir du terrain sur la Grid",
        nameIt: "Nommez ce territoire",
        loading: "Résolution de vos territoires…",
        unavailable: "Vos données de propriété n'ont pas pu être résolues pour l'instant. Réessayez bientôt.",
      },
      start: {
        title: "Choisissez comment commencer",
        body: "Partez d'un Canvas vierge, ou chargez l'un des six modèles premium et faites-le vôtre. Les deux ouvrent l'éditeur réel — rien n'est publié tant que vous ne le décidez pas.",
        blank: "Partir de zéro",
        template: "Utiliser un modèle",
        hint: "Les modèles vivent dans l'éditeur sous \"Modèles\".",
      },
      build: {
        title: "Construisez votre territoire",
        body: "Concevez votre Canvas : identité, médias, liens et contacts. Enregistrez votre brouillon quand il vous convient — il reste privé jusqu'à la publication.",
        open: "Ouvrir l'éditeur Canvas",
        draftSaved: "Brouillon enregistré",
        noDraft: "Aucun brouillon enregistré",
      },
      publish: {
        title: "Publier",
        body: "La publication prend un snapshot public et durable de votre brouillon enregistré. Vous pouvez continuer à éditer en privé ; le public voit votre version publiée jusqu'à la republication.",
        open: "Ouvrir la publication",
        published: "Publié",
        notReady: "Enregistrez d'abord un brouillon, puis publiez depuis votre territoire.",
      },
      launch: {
        title: "Lancez et développez",
        body: "Votre territoire est en ligne. Partagez-le — il apparaît sur Explore automatiquement, du plus récent, sans classement payant.",
        viewPublic: "Voir votre page publique",
        explore: "Le voir sur Explore",
        copyLink: "Copier le lien public",
        copied: "Lien copié",
        locked: "Publiez votre territoire pour débloquer cette étape.",
      },
    },
    guide: {
      title: "Parcours guidé",
      heading: "Construisez votre territoire",
      intro: "Ajoutez votre identité, vos visuels et liens, puis enregistrez votre brouillon.",
      items: {
        identity: "Ajoutez votre identité (titre, slogan)",
        media: "Ajoutez un visuel ou média",
        social: "Ajoutez infos sociales / contact",
        save: "Enregistrer le brouillon",
      },
      back: "Retour au guide",
      hide: "Masquer le guide",
    },
    entry: {
      eyebrow: "Nouveau ici ?",
      title: "Lançons votre premier territoire",
      body: "Un court parcours guidé vous mène de la réclamation de terrain à une page publiée. Rien n'est imposé.",
      start: "Démarrer le guide",
      notNow: "Pas maintenant",
      resume: "Reprendre le guide",
      restart: "Recommencer le guide",
      completed: "Guide terminé",
    },
    creatorsCta: {
      title: "Nouveau sur THE GRID ? Suivez le parcours guidé.",
      body: "D'un territoire possédé à une page publiée en quelques étapes guidées — modèles, construction, publication.",
      cta: "Démarrer le parcours guidé",
    },
  },
  marketplace: {
    home: {
      eyebrow: "The Grid · Marketplace",
      title: "Possédez, échangez et collectionnez les coordonnées d'un monde vivant.",
      subtitle: "Chaque annonce fait autorité côté backend et est fail-closed. Les parcelles de terrain et les œuvres figées se règlent via la même autorité de settlement on-chain — rien n'est jamais fabriqué.",
      browseLand: "Parcourir les Terrains",
      browseArtworks: "Parcourir les Œuvres",
      cardLandTitle: "Terrains",
      cardLandDesc: "Parcelles à prix fixe avec pricing autoritatif par zone et floors cross-currency.",
      cardArtworksTitle: "Œuvres",
      cardArtworksDesc: "Œuvres figées et placées, échangées avec royalties au créateur et enchères en direct.",
      cardMineTitle: "Mon Marché",
      cardMineDesc: "Vos annonces, offres faites et reçues, et enchères en un seul endroit.",
    },
    nav: {
      search: "Rechercher",
      searchAria: "Rechercher dans le marketplace",
      notifications: "Notifications",
      notificationsCountAria: "{count} notifications",
    },
    search: {
      title: "Rechercher dans le marketplace",
      subtitle: "Parcelles · œuvres · adresses",
      placeholder: "Parcel #, titre d'œuvre ou adresse de wallet…",
      startTyping: "Commencez à taper pour rechercher annonces et œuvres.",
      noMatches: "Aucun résultat pour « {q} ».",
      land: "terrain",
      artwork: "œuvre",
      sellerPrefix: "Vendeur {addr}",
      byPrefix: "par {addr}",
      auctionSuffix: " · enchère",
    },
    notifications: {
      title: "Notifications",
      offersOne: "{n} offre à examiner",
      offersMany: "{n} offres à examiner",
      offersDetail: "Des acheteurs ont fait des offres sur vos actifs.",
      outbidOn: "Surenchéri sur {title}",
      anAuction: "une enchère",
      topBid: "Meilleure enchère {top} · votre enchère {yours}",
      allCaughtUp: "Vous êtes à jour.",
      markAll: "Tout marquer comme lu",
    },
    land: {
      sectionTitle: "Annonces de Terrain",
      loadErrorTitle: "Impossible de charger les annonces",
      loadErrorDetail: "L'index du marketplace est injoignable pour le moment. Rien n'a été modifié.",
      emptyNoneTitle: "Aucun terrain en vente pour l'instant",
      emptyNoneHint: "Lorsque des propriétaires mettent des parcelles en vente, elles apparaissent ici.",
      emptyFilteredTitle: "Aucune annonce ne correspond à vos filtres",
      emptyFilteredHint: "Essayez d'élargir la fourchette de prix ou de retirer le filtre de zone.",
      slots: "{n} slots",
      filters: {
        zone: "Zone",
        price: "Prix (USDC)",
        min: "Min",
        max: "Max",
        minSlots: "Slots min",
        any: "Tous",
        sort: "Trier",
        clear: "Effacer les filtres",
        sortRecent: "Plus récents",
        sortPriceAsc: "Prix · croissant",
        sortPriceDesc: "Prix · décroissant",
        sortSlotsDesc: "Les plus grands d'abord",
      },
    },
    zones: {
      all: "Toutes les zones",
      standard: "Standard",
      premium: "Premium",
      vip: "VIP",
      reserve: "Reserve",
      gridZero: "Grid Zero",
    },
    artworks: {
      loadErrorTitle: "Impossible de charger les œuvres",
      loadErrorDetail: "L'index de la galerie est injoignable pour le moment.",
      liveAuctions: "Enchères en Direct",
      live: "{n} en direct",
      gallery: "Galerie",
      works: "{n} œuvres",
      emptyTitle: "Aucune œuvre pour l'instant",
      emptyHint: "Les œuvres figées placées sur la Grid apparaissent ici pour offres et enchères.",
      auction: "Enchère",
      openToOffers: "Ouverte aux offres",
      royalty: "{n}% royalty",
      by: "par {addr}",
    },
    detail: {
      backToLand: "Retour aux Terrains",
      backToArtworks: "Retour aux Œuvres",
      listingNotFound: "Annonce introuvable",
      listingNotFoundDetail: "Cette annonce a peut-être été vendue ou retirée.",
      artworkNotFound: "Œuvre introuvable",
      artworkNotFoundDetail: "Cette œuvre a peut-être été déplacée ou supprimée.",
      loadingParcel: "Chargement du parcel…",
      loadingArtwork: "Chargement de l'œuvre…",
      seller: "Vendeur {addr}",
      creatorOwner: "par {creator} · propriétaire {owner}",
      slots: "Slots",
      footprint: "Emprise",
      floor: "Floor",
      size: "Taille",
      cells: "Cellules",
      royalty: "Royalty",
      status: "Statut",
      yourListing: "Votre annonce",
      buyNow: "Acheter",
      unavailable: "Indisponible",
      makeOffer: "Faire une offre",
      viewOnGrid: "Voir sur la Grid",
      sellerNote: "Vous êtes le vendeur — l'achat et les offres sont désactivés sur votre propre annonce.",
      ownerNote: "Vous possédez cette œuvre — les offres et enchères sont désactivées pour vous.",
      openOffers: "Offres Ouvertes",
      noOffersParcel: "Aucune offre ouverte sur ce parcel.",
      noOffersArtwork: "Aucune offre ouverte sur cette œuvre.",
      auctionTag: "Enchère",
      openToOffers: "Ouverte aux offres",
      currentBid: "Enchère actuelle",
      reserve: "Réserve",
      leadingPrefix: "En tête {addr} · ",
      noBidsYet: "Pas encore d'enchères · ",
      minNext: "min suivant {amt} {sym}",
      auctionEnded: "Enchère terminée",
      yourAuction: "Votre enchère",
      placeBid: "Enchérir",
      buyTitle: "Acheter Parcel #{id}",
      buySubtitle: "Settlement à prix fixe",
      done: "Terminé",
      cancel: "Annuler",
      reserving: "Réservation…",
      authorizing: "Autorisation…",
      reserveAuthorize: "Réserver et autoriser",
      signInRequired: "Connexion requise",
      connectSignIn: "Connectez votre wallet et connectez-vous →",
      settlementUnavailable: "Le settlement est temporairement indisponible — rien ne peut être débité pour l'instant.",
      price: "Prix",
      gridFee: "Frais Grid",
      settlementAuthorized: "Settlement autorisé",
      settlementAuthorizedBody: "L'autorité de settlement a signé votre permit. Finalisez l'achat depuis votre wallet pour conclure on-chain.",
      authorityFloor: "Autorité {addr} · floor {amt} {sym}",
      completePurchase: "Complete purchase",
      checkingAllowance: "Checking allowance…",
      confirmInWallet: "Confirm in your wallet…",
      approvingUsdc: "Approving USDC…",
      settlingOnchain: "Finalizing on-chain…",
      purchaseComplete: "Purchase complete",
      purchaseCompleteBody: "The LAND is now yours. Ownership and listings will refresh shortly.",
      buyFailed: "Purchase failed",
      approveRejected: "USDC approval was rejected in your wallet.",
      approveReverted: "The USDC approval transaction reverted on-chain.",
      fillRejected: "The purchase was rejected in your wallet.",
      fillReverted: "The purchase transaction reverted on-chain.",
      wrongBuyerWallet: "Connect the buyer wallet {addr} to complete the purchase.",
      makeOfferTitle: "Faire une offre",
      makeOfferSubtitle: "Offre ferme · expiration en market-time",
      submitOffer: "Envoyer l'offre",
      submitting: "Envoi…",
      amount: "Montant",
      expiresIn: "Expire dans",
      dur1h: "1 heure",
      dur24h: "24 heures",
      dur3d: "3 jours",
      dur7d: "7 jours",
      placeBidTitle: "Placer une enchère",
      auctionFallback: "Enchère",
      signing: "Signature…",
      yourBid: "Votre enchère ({sym})",
      minNextBid: "Enchère minimale suivante : {amt} {sym}",
      bidPermitSigned: "Permit d'enchère signé",
      bidPermitBody: "L'autorité de settlement a signé votre permit d'enchère. Soumettez-le depuis votre wallet pour enregistrer l'enchère on-chain.",
      antiSnipe: " Votre enchère tombe dans la fenêtre anti-snipe et prolongera l'enchère.",
      authorityMin: "Autorité {addr} · min accepté {amt} {sym}",
    },
    provenance: {
      title: "Historique / Provenance",
      empty: "Aucun historique de propriété pour cette parcelle.",
      resale: "Revente",
      primaryPurchase: "Achat primaire",
      privateSale: "Vente privée",
      transfer: "Transfert",
      from: "De",
      to: "À",
      price: "Prix",
      fee: "Frais",
      net: "Net",
      viewTx: "Voir tx →",
      currentNameLabel: "Nom actuel",
    },
    owner: {
      cancelPrivateSale: "Annuler",
      cancelBlocked: "Non annulable",
      cancelUnsupportedNote: "L'annulation on-chain de la vente privée n'est pas prise en charge par le contrat déployé. Les demandes d'annulation échouent de manière sécurisée et ne modifient jamais l'enregistrement.",
      cancelPsTitle: "Annuler la vente privée",
      cancelPsBadge: "Action owner",
      cancelPsWarning: "Ceci demande l'annulation on-chain de la vente privée. Elle ne prend effet qu'après confirmation de la transaction on-chain.",
      cancelPsSuccess: "Vente privée annulée on-chain.",
      cancelPsConfirm: "Demander l'annulation",
      cancelPsClose: "Fermer",
      cancelPsUnsupported: "Le contrat déployé ne prend pas en charge l'annulation on-chain de la vente privée. Aucun enregistrement n'a été modifié.",
      psStatusActive: "Active",
      psStatusCompleted: "Terminée",
      psStatusExpired: "Expirée",
      psStatusCancelled: "Annulée",
      verifyingAccess: "Vérification de l'accès owner…",
      accessDeniedTitle: "Accès owner requis",
      accessDeniedHint: "Cette console est réservée aux wallets owner en allow-list serveur. Connectez-vous avec un wallet admin.",
      loadErrorTitle: "Impossible de charger l'Owner Console",
      loading: "Chargement de l'Owner Console…",
      integrityTitle: "Intégrité de la Supply",
      noFreeGrant: "Aucun grant gratuit ✓",
      grantAnomaly: "Anomalie de grant ✕",
      supplyActiveListings: "Annonces Actives",
      supplySoldListings: "Annonces Vendues",
      supplyOpenOffers: "Offres Ouvertes",
      supplyOpenAuctions: "Enchères Ouvertes",
      supplyPendingSettlements: "Settlements en Attente",
      supplyPaidAcquisitions: "Acquisitions Payées",
      zeroPriceGrants: "Grants à prix zéro",
      actionsTitle: "Actions Owner",
      gridZeroManual: "Grid Zero manuel",
      releaseLabel: "Release",
      releaseDesc: "Rendre disponible l'inventaire éligible. Cell-authoritative : libérées, non effectivement réservées, non occupées.",
      relockLabel: "Relock",
      relockDesc: "Reverrouiller l'inventaire disponible. Ne reverrouille jamais les cellules vendues, occupées ou non éligibles.",
      privateSaleLabel: "Vente Privée",
      privateSaleDesc: "Créer une vente réservée à un acheteur. Le prix de tier est officiel ; Grid Zero est manuel. Le prix doit être > 0.",
      failClosed: "Fail-closed",
      unavailable: "Indisponible",
      privateSalesTitle: "Ventes Privées",
      noPrivateSalesTitle: "Aucune vente privée projetée",
      noPrivateSalesHint: "Les ventes privées apparaissent ici une fois créées on-chain — annulables jusqu'au pending.",
      buyer: "acheteur {addr}",
      perSlot: "/ slot",
      executed: "Exécutée",
      pending: "En attente",
      supplyCheckTitle: "Contrôle de Supply",
      walletPlaceholder: "adresse de wallet 0x",
      checking: "Vérification…",
      auditWallet: "Auditer le wallet",
      supplyCheckFailed: "Échec du contrôle de supply",
      owned: "Possédés",
      paidOrigin: "Origine payée",
      integrity: "Intégrité",
      consistent: "Cohérent",
      anomalies: "Anomalies",
      confirmationSuffix: "confirmation {tier}",
      tierNormal: "normale",
      tierStrong: "forte",
      tierMaximum: "maximale",
      failClosedBanner: "Fail-closed : {reason}. Cette action ne peut pas être exécutée — rien n'est débité ni fabriqué.",
      typeToProceedPre: "Tapez ",
      typeToProceedPost: " pour continuer",
      ack: "Je comprends qu'il s'agit d'une action owner cell-authoritative et je confirme la cible et la quantité.",
      confirmAction: "Confirmer {action}",
      submitting: "Envoi…",
      cancel: "Annuler",
      zoneLabel: "Zone",
      slotsLabel: "Slots",
      buyerLabel: "Wallet de l'acheteur",
      priceLabel: "Prix unitaire (USDC)",
      priceHintGridZero: "Grid Zero exige un prix unitaire manuel > 0.",
      priceHintTier: "Le prix de tier est officiel et appliqué automatiquement.",
      actionSuccess: "Action owner exécutée",
      actionSuccessBody: "{action} : {count} cellules en {zone} — enregistrée dans l'audit owner immuable. Aucun grant gratuit, aucune écriture on-chain.",
      demoScope: "Preview Demo — substrate isolé, aucune écriture on-chain",
    },
    common: {
      parcelNumber: "Parcel #{id}",
      partialTag: "PARTIEL",
      close: "Fermer",
      retry: "Réessayer",
    },
    confirm: {
      normal: "Veuillez confirmer cette action owner cell-authoritative.",
      strong: "Cela affecte {slots} slots (plus de {threshold}). Ressaisissez le nombre exact de slots pour continuer.",
      maximum: "GRID ZERO est l'inventaire le plus sensible de THE GRID. La confirmation maximale est requise quelle que soit la quantité.",
    },
    statePill: {
      active: "actif",
      sale_pending: "vente en attente",
      confirming: "confirmation",
      sold: "vendu",
      expired: "expiré",
      cancelled: "annulé",
      reconciliation_required: "réconciliation requise",
      open: "ouvert",
    },
    errors: {
      unauthenticatedTitle: "Connexion requise",
      unauthenticatedHint: "Connectez votre wallet et connectez-vous pour autoriser cette action.",
      disabledTitle: "Marché en pause",
      disabledHint: "Le settlement est temporairement indisponible. Rien n'a été débité ni modifié — réessayez à la reprise.",
      cellLockedTitle: "Réservé par un autre acheteur",
      cellLockedHint: "Une ou plusieurs cellules de ce parcel sont retenues en ce moment par l'achat en attente d'un autre acheteur. Réessayez dans quelques minutes.",
      notActiveTitle: "Plus disponible",
      notActiveHint: "Cette annonce n'est plus active — elle vient peut-être d'être vendue ou retirée. Actualisez pour voir la dernière version.",
      expiredTitle: "Annonce expirée",
      expiredHint: "Cette annonce a expiré et ne peut plus être achetée. Le propriétaire peut la republier.",
      selfTitle: "C'est votre annonce",
      selfHint: "Vous ne pouvez pas acheter ni enchérir sur un actif que vous vendez déjà.",
      belowFloorTitle: "Sous le prix floor",
      belowFloorHint: "Le prix a bougé et est désormais sous le floor du vendeur. Vérifiez et réessayez.",
      settlementTitle: "Settlement en cours",
      settlementHint: "Un settlement pour cet actif est déjà en cours. Attendez sa finalisation avant de réessayer.",
      reconciliationTitle: "Réconciliation requise",
      reconciliationHint: "Cet actif doit être réconcilié avec la chain avant de pouvoir être échangé. Aucune action n'a été effectuée.",
      spatialTitle: "Conflit de placement",
      spatialHint: "Cette œuvre n'est pas dans un placement réglable actuellement — elle a peut-être bougé ou chevauche un autre placement.",
      bidLowTitle: "Enchère trop basse",
      bidLowHint: "Votre enchère est sous l'enchère minimale suivante. Augmentez-la et réessayez.",
      permitReusedTitle: "Permit déjà utilisé",
      permitReusedHint: "Ce permit d'enchère a déjà été dépensé. Placez une nouvelle enchère.",
      auctionClosedTitle: "Enchère fermée",
      auctionClosedHint: "Cette enchère n'accepte plus d'enchères.",
      auctionNotStartedTitle: "Enchère non commencée",
      auctionNotStartedHint: "Cette enchère n'est pas encore ouverte aux enchères.",
      sellerBidTitle: "Les vendeurs ne peuvent pas enchérir",
      sellerBidHint: "Vous ne pouvez pas enchérir sur votre propre enchère.",
      permitRefusedTitle: "Autorisation refusée",
      permitRefusedHint: "L'autorité de settlement a refusé de signer. Aucune action n'a été effectuée.",
      noBasisTitle: "Origine non enregistrée",
      noBasisHint: "Cet actif n'a pas encore d'origine d'acquisition PAYÉE enregistrée, il ne peut donc pas être réglé. Aucune action n'a été effectuée.",
      zeroAmountTitle: "Montant requis",
      zeroAmountHint: "Saisissez un montant supérieur à zéro.",
      genericTitle: "Une erreur est survenue",
      genericHint: "L'action n'a pas pu être effectuée. Rien n'a été modifié.",
    },
    tabs: {
      overview: "Aperçu",
      land: "Terrains",
      artworks: "Œuvres",
      mine: "Mon Marché",
      owner: "Owner Console",
      ownerAria: "Owner Console — admin uniquement",
    },
    states: {
      loading: "Chargement…",
      retry: "Réessayer",
      active: "actives",
    },
    paused: {
      title: "Marché en pause",
      body: "Le settlement est temporairement indisponible. La navigation reste ouverte ; rien ne peut être débité.",
    },
    mine: {
      signInTitle: "Connectez-vous pour voir votre marché",
      signInHint: "Connectez votre wallet et connectez-vous pour voir vos annonces, offres et enchères.",
      connectSignIn: "Se connecter →",
      loadErrorTitle: "Impossible de charger votre marché",
      loadingYours: "Chargement de votre marché…",
      listings: "Mes Annonces",
      listLand: "Vendre un Terrain",
      noListings: "Aucune annonce active",
      noListingsHint: "Mettez un parcel en vente depuis sa page de détail — ou via Vendre un Terrain — pour le vendre.",
      offersReceived: "Offres Reçues",
      offersMade: "Offres Faites",
      bids: "Mes Enchères",
      noBids: "Aucune enchère active",
      noBidsHint: "Enchérissez sur une enchère en direct pour la voir ici.",
      cancel: "Annuler",
      withdraw: "Retirer",
      noOpenOffers: "Aucune offre ouverte",
      noOffersReceived: "Aucune offre reçue",
      offerMadeHint: "Faites une offre depuis n'importe quelle annonce ou œuvre.",
      offerReceivedHint: "Les offres sur vos actifs apparaîtront ici.",
      accept: "Accepter",
      decline: "Refuser",
      accepting: "Acceptation…",
      acceptUnsupported: "Ce type d'offre ne peut pas encore être accepté ici.",
      pendingPurchases: "Achats en Attente",
      noPendingPurchases: "Aucun achat en attente",
      pendingPurchasesHint: "Les offres acceptées par un vendeur apparaissent ici pour finaliser l'achat.",
      completePurchase: "Finaliser l'achat",
      auction: "Enchère",
      leading: "En tête",
      outbid: "Surenchéri",
      bidPrefix: "enchère",
      artworkLabel: "Œuvre",
      sales: "Ventes",
      purchases: "Achats",
      noSales: "Aucune vente",
      noSalesHint: "Les parcelles que vous vendez sur le marché secondaire apparaîtront ici.",
      noPurchases: "Aucun achat",
      noPurchasesHint: "Les parcelles que vous achetez sur le marché secondaire apparaîtront ici.",
      soldTo: "Vendu à",
      boughtFrom: "Acheté à",
      feeShort: "Frais",
      netShort: "Net",
      soldStatus: "Vendu",
      viewTx: "Voir tx →",
    },
    sell: {
      slotsSelected: "Cases sélectionnées",
      partialBadge: "Zone partielle",
      realSelectTitle: "Sélectionnez la zone à vendre",
      realSelectHint: "Cliquez ou faites glisser pour sélectionner des cellules. Sélectionnez la parcelle entière pour la mettre en vente, ou une zone contiguë pour une vente partielle.",
      selectWhole: "Sélectionner toute la parcelle",
      wholeSelectedNote: "Parcelle entière sélectionnée — elle sera mise en vente en totalité.",
      wholeIneligibleTitle: "Vente de la parcelle entière indisponible",
      wholeIneligibleBody: "Une partie de cette parcelle est inéligible — un verrou de marché actif ou un contenu canonique (une œuvre placée) occupe une ou plusieurs cellules. La mise en vente de la parcelle entière est bloquée tant que ces cellules ne sont pas libres. Aucune annonce n'a été créée.",
      partialDisabledTitle: "Vente partielle indisponible",
      partialVaultRequired: "La vente d'une zone partielle nécessite un jeton enfant on-chain SubdivisionVault pour cette parcelle, qui n'existe pas encore. Créez la subdivision on-chain avant de mettre cette zone en vente. Aucune annonce n'a été créée.",
      subdivisionTooComplex: "La forme sélectionnée dépasse la limite de subdivision on-chain ({total}/{max} rectangles) — ajustez votre sélection. Aucune transaction envoyée.",
      subAlreadySubdivided: "Cette parcelle est déjà subdivisée on-chain. La re-subdivision n'est pas encore prise en charge.",
      subDepositFailed: "Le dépôt de la parcelle dans le coffre a échoué.",
      subSplitFailed: "La division de subdivision on-chain a échoué.",
      subGeometryMismatch: "La géométrie de l'enfant on-chain ne correspond pas à votre sélection.",
      sub_plan: "Calcul de la subdivision…",
      sub_approve: "Approbation du coffre…",
      sub_approveCollection: "Approbation de la place de marché…",
      nftApprovalFailed: "L'autorisation de transfert vers la place de marché a échoué. Aucune annonce n'a été créée.",
      sub_deposit: "Dépôt de la parcelle…",
      sub_split: "Subdivision on-chain…",
      sub_reconcile: "Réconciliation…",
      sub_sign: "Signer et publier…",
      wholeTitle: "List the whole parcel",
      wholeSubtitle: "Parcel #{id} · whole parcel",
      wholeBadge: "Whole parcel",
      wholeIntro: "You are authorizing a REAL secondary-market listing of the ENTIRE parcel on Base Sepolia. Your wallet signs an EIP-712 order; nothing is written on-chain until a buyer fills it.",
      wholeConnectRequired: "Connect the owner wallet to authorize this listing.",
      wholeOwnerMismatch: "The connected wallet is not the on-chain owner of this parcel.",
      wholeNotConfigured: "The marketplace contract is not configured for real listings.",
      signAndList: "Sign & list",
      signing: "Awaiting wallet signature…",
      reviewToken: "Token ID",
      reviewCollection: "Collection",
      realCreatedTitle: "Listing authorized",
      realCreatedBody: "Your signed order is live in the marketplace. It settles on-chain only when a buyer fills it.",
      partialFutureTitle: "Sell a subset (partial)",
      partialFutureBody: "Partial sale requires an on-chain subdivision-vault child token, which is not yet available on Base Sepolia. Only whole-parcel listings are possible right now.",
      priceRealHelp: "Set your asking price in {sym}.",
      listLand: "Vendre un Terrain",
      sellLand: "Vendre un Terrain",
      sellLandDemo: "Vendre un Terrain · Preview Demo",
      previewDemo: "Preview Demo",
      previewDemoGrid: "Grid Preview Demo",
      myLand: "Mes Terrains",
      selectLand: "Sélectionner un Terrain",
      pickTitle: "Choisissez le terrain à vendre",
      pickHint: "Sélectionnez l'un de vos parcels pour mettre en vente un sous-ensemble contigu de cellules.",
      pickEmpty: "Aucun terrain vendable",
      pickEmptyHint: "Vous ne possédez aucun terrain disponible pour une vente partielle dans cette preview.",
      pickCells: "{n} cellules",
      pickListed: "{n} actifs",
      pickOpen: "Sélectionner une zone →",
      wizardTitle: "Vendre un Terrain",
      wizardSubtitle: "Parcel #{id} · Preview Demo",
      stepSelect: "Sélectionner la zone",
      stepPrice: "Prix",
      stepReview: "Récapitulatif",
      selectTitle: "Sélectionnez les cellules à vendre",
      selectHint: "Cliquez sur une cellule pour la basculer, ou cliquez-glissez pour peindre un rectangle. La sélection doit être un bloc contigu et ne peut pas être le parcel entier.",
      selectedCount: "{n} sur {total} cellules sélectionnées",
      clearSelection: "Effacer",
      valLoading: "Validation…",
      valValid: "Sélection valide",
      valInvalid: "Sélection non valide",
      basisLabel: "Coût d'acquisition",
      floorLabel: "Floor de revente minimum (80%)",
      floorBinding: "Contraignant",
      floorNotBinding: "Floor non contraignant (aucune basis enregistrée)",
      reasonOutside: "Les cellules sont hors du parcel",
      reasonNotContiguous: "Les cellules doivent former un seul bloc contigu",
      reasonFull: "C'est le parcel entier — utilisez l'annonce de parcel complet",
      reasonNotOwner: "Vous n'êtes pas le propriétaire de ce terrain",
      reasonOverlap: "Certaines cellules chevauchent une annonce existante",
      reasonOccupied: "Certaines cellules sont occupées par du contenu canonique",
      reasonNoBasis: "Prix plancher de revente indisponible : aucune base de coût d'acquisition enregistrée pour cette parcelle.",
      priceTitle: "Fixez votre prix",
      priceLabel: "Prix",
      currencyLabel: "Devise",
      priceHelpFloor: "Doit être au moins le floor de {floor} {sym}.",
      belowFloor: "Le prix est sous le floor minimum de {floor} {sym}.",
      reviewTitle: "Vérifier et autoriser",
      reviewCells: "Cellules",
      reviewArea: "Zone",
      reviewFloor: "Floor",
      reviewPrice: "Votre prix",
      authorize: "Autoriser et Publier",
      authorizeDemo: "Autoriser (Preview Demo) et Publier",
      creating: "Création de l'annonce…",
      createdTitle: "Annonce demo créée",
      createdBody: "Votre annonce preview demo est maintenant visible dans Mes Annonces.",
      viewMine: "Aller à Mes Annonces",
      demoDisclosureTitle: "Preview Demo — aucun changement on-chain / canonique",
      demoDisclosureBody: "Ceci publie un parcel demo isolé sur le substrate de preview. Votre session SIWE autorise l'écriture ; aucun mint on-chain du SubdivisionVault, aucun ordre EIP-712, aucun settlement sur Base Sepolia. En production, ce même flux signe avec votre wallet et règle on-chain.",
      back: "Retour",
      next: "Suivant",
      close: "Fermer",
    },
    artworkMkt: {
      buyNow: "Buy now",
      listedAt: "Listed at",
      fixedPrice: "Fixed price",
      yourListing: "Your listing",
      cancelListing: "Cancel listing",
      sellArtwork: "Sell / List Artwork",
      listArtwork: "List / Sell",
      buyTitle: "Buy “{title}”",
      buySubtitle: "Fixed-price artwork settlement",
      buyIntro: "You are buying this artwork at its listed price. Your wallet locks USDC in escrow, then the sale finalizes on-chain. Nothing moves until you confirm.",
      priceLabel: "Price",
      royaltyLabel: "Creator royalty",
      gridFeeLabel: "Grid fee",
      confirmBuy: "Confirm & buy",
      buyPreparing: "Preparing settlement…",
      buyCheckingAllowance: "Checking USDC allowance…",
      buyApprove: "Approve USDC in your wallet…",
      buyApproving: "Approving USDC…",
      buyOpenWallet: "Confirm escrow in your wallet…",
      buyOpening: "Locking funds in escrow…",
      buyIndexingEscrow: "Confirming escrow on-chain…",
      buyAuthorizing: "Authorizing payout…",
      buyReleaseWallet: "Confirm payout in your wallet…",
      buyReleasing: "Releasing payout on-chain…",
      buyIndexingPayout: "Confirming payout finality…",
      buyFinalizing: "Transferring ownership…",
      buyComplete: "Purchase complete",
      buyCompleteBody: "The artwork is now yours. Ownership and listings refresh shortly.",
      buyRejected: "The action was rejected in your wallet.",
      buyReverted: "The transaction reverted on-chain.",
      buyFailed: "Purchase failed",
      connectBuyer: "Connect the buyer wallet {addr} to complete this purchase.",
      sellIntro: "You own this artwork. List it at a fixed price or open a live auction.",
      cancelListingConfirm: "Cancel this listing?",
      listingCancelled: "Listing cancelled.",
      wizardTitle: "Sell Artwork",
      pickTitle: "Choose an artwork to sell",
      pickHint: "Select a frozen, placed artwork you own that isn't already listed or in auction.",
      pickEmpty: "No sellable artworks",
      pickEmptyHint: "You need a frozen artwork placed on the Grid that isn't already listed or auctioned.",
      chooseMode: "How do you want to sell?",
      modeFixed: "Fixed price",
      modeFixedDesc: "List at a set price. Buyers purchase instantly; you sign nothing on-chain.",
      modeAuction: "Auction",
      modeAuctionDesc: "Run a timed English auction with a reserve. You sign the auction creation on-chain.",
      setPrice: "Set your price",
      priceHint: "Set your asking price in {sym}. Grid fee {fee}% + creator royalty {roy}% are deducted on settlement.",
      listBtn: "List for sale",
      listing: "Listing…",
      listedTitle: "Artwork listed",
      listedBody: "Your artwork is now listed at the fixed price. Buyers can purchase it from its detail page.",
      setAuction: "Auction terms",
      reserveLabel: "Reserve price",
      minIncrementLabel: "Minimum bid increment",
      durationLabel: "Duration",
      antiSnipeLabel: "Anti-snipe window",
      dur1h: "1 hour",
      dur6h: "6 hours",
      dur24h: "24 hours",
      dur3d: "3 days",
      dur7d: "7 days",
      antiSnipe60: "60 seconds",
      antiSnipe300: "5 minutes",
      antiSnipeOff: "Off",
      createAuctionBtn: "Create auction",
      creatingAuction: "Creating auction…",
      auctionWalletConfirm: "Confirm auction in your wallet…",
      auctionSubmitting: "Creating auction on-chain…",
      auctionCreatedTitle: "Auction created",
      auctionCreatedBody: "Your auction is live on-chain. Bidders can place bids until it ends.",
      review: "Review",
      reviewArtwork: "Artwork",
      reviewRoyalty: "Royalty",
      reviewMode: "Mode",
      reviewPrice: "Price",
      reviewReserve: "Reserve",
      reviewIncrement: "Increment",
      reviewEnds: "Ends",
      settleAuction: "Settle auction",
      settling: "Settling…",
      settleWalletConfirm: "Confirm settlement in your wallet…",
      settled: "Auction settled",
      cancelAuction: "Cancel auction",
      cancellingAuction: "Cancelling…",
      auctionCancelled: "Auction cancelled.",
      bidPlacedOnchain: "Bid placed on-chain.",
      bidApprove: "Approve USDC in your wallet…",
      bidApproving: "Approving USDC…",
      bidWalletConfirm: "Confirm bid in your wallet…",
      bidSubmitting: "Placing bid on-chain…",
      historyTitle: "History / Provenance",
      historyEmpty: "No sale or auction history yet for this artwork.",
      histPrepared: "Settlement prepared",
      histFinalized: "Sold (fixed price)",
      histAuctionSettled: "Auction settled",
      histFrozen: "Frozen & placed",
      myArtworkListings: "My Artwork Listings",
      noArtworkListings: "No artwork listings",
      noArtworkListingsHint: "List a frozen artwork for sale to see it here.",
      myAuctions: "My Auctions",
      noMyAuctions: "No auctions",
      noMyAuctionsHint: "Create an auction from an artwork you own to see it here.",
      close: "Close",
      cancel: "Cancel",
      back: "Back",
      next: "Next",
      done: "Done",
      viewTx: "View tx →",
      signInFirst: "Connect your wallet & sign in →",
      auctionEnds: "Ends {when}",
    },
    demoGrid: {
      title: "Grid Preview Demo",
      subtitle: "Votre terrain demo · substrate de preview isolé",
      disclosure: "Ceci est une surface PREVIEW DEMO isolée — pas la Grid canonique. Rien ici ne touche de vrais parcels, la propriété on-chain ou Base Sepolia.",
      empty: "Vous n'avez aucun terrain demo dans cette preview.",
      emptyHint: "Connectez-vous avec le wallet demo vendeur pour voir le terrain demo vendable.",
      returnToMarket: "← Retour au Marketplace",
      ownedBadge: "À vous",
      listedBadge: "{n} en vente",
      clickToSell: "Cliquez sur un parcel pour en vendre une partie",
      worldLabel: "Monde 1000 × 1000",
      loadError: "Impossible de charger votre terrain demo",
    },
    clock: {
      closing: "clôture",
      ended: "Terminée",
    },
    grid: {
      cellAria: "cellule {x},{y}",
      cellLockedAria: "cellule {x},{y} (verrouillée)",
    },
  },
  purchase: {
    intro: "Aperçu du devis en direct — rien n'est débité tant que vous ne confirmez pas.",
    loading: "chargement…",
    bootstrapError: "Échec du chargement de /sale/config ou /sale/status : {error}",
    recovery: {
      confirming: "Achat en attente récupéré — confirmation sur Base…",
      syncing: "Confirmé on-chain — synchronisation avec la Grid…",
      failed: "La transaction d'achat récupérée a été annulée. Aucune parcelle n'a été achetée.",
      degraded: "Réseau temporairement indisponible — nouvelle tentative sur le statut de l'achat…",
    },
    sections: {
      contracts: "Contrats on-chain",
      saleStatus: "Statut de la vente",
      wallet: "Wallet",
      financial: "Solde et autorisation USDC",
      approval: "Autorisation USDC",
      purchase: "Achat",
      indexing: "Indexation",
      quote: "Devis",
    },
    gate: { yes: "OUI", no: "NON ({reason})" },
    wallet: {
      connect: "Connecter {name}",
      disconnect: "Déconnecter",
      wrongChain: "Le wallet est sur la chain {chainId}, la vente est sur la {expectedChainId}. Changez de réseau dans votre wallet.",
      switching: "Changement…",
      switchTo: "Passer à la chain {chainId}",
      switchRejected: "changement refusé",
      switchError: "erreur de changement : {message}",
    },
    financial: {
      blocked: "lectures désactivées : {reason}",
      readError: "erreur de lecture",
      noSellableQuote: "— (aucun devis vendable)",
      insufficient: "insufficient_balance (il manque {amount})",
      approval: "approval_required (nécessite {amount})",
      ready: "ready_for_purchase",
      rpcError: "Erreur RPC lors de la lecture de l'état USDC.",
      refetch: "Recharger solde/autorisation",
    },
    approve: {
      blocked: "autorisation désactivée : {reason}",
      waitingWallet: "En attente du wallet…",
      submitting: "Envoi…",
      confirming: "Confirmation…",
      cta: "Autoriser le montant exact d'USDC",
      rejected: "L'utilisateur a refusé la transaction d'autorisation. Vous pouvez réessayer.",
      reverted: "La transaction d'autorisation a été annulée on-chain.",
      error: "Erreur d'autorisation : {error}",
      confirmed: "Autorisation confirmée. Rechargement de l'autorisation et nouveau devis forcé déclenchés.",
    },
    tx: {
      blocked: "achat désactivé : {reason}",
      refreshingQuote: "Actualisation du devis final…",
      awaitingWallet: "Confirmez dans votre wallet…",
      submitted: "Envoyé…",
      confirming: "Confirmation sur Base…",
      cta: "Acheter",
      rejected: "L'utilisateur a refusé la transaction d'achat.",
      reverted: "Transaction annulée",
      revertedReason: "Transaction annulée : {reason}",
      error: "Erreur d'achat : {error}",
      confirmed: "Transaction confirmée on-chain. Synchronisation avec la Grid — en attente de l'indexation backend…",
    },
    indexing: {
      pending: "Synchronisation avec la Grid…",
      delayed: "Transaction confirmée on-chain. L'indexation backend prend plus de temps que prévu.",
      checkAgain: "Vérifier à nouveau",
      success: "Possédée · Live. Achat indexé avec succès.",
      buyAnother: "Acheter une autre parcelle",
      anomaly: "Anomalie d'indexation détectée ({reason}). La transaction on-chain est confirmée mais l'enregistrement indexé ne correspond pas au snapshot d'achat initial. Veuillez contacter le support avec le hash de transaction ci-dessous.",
    },
    quote: {
      empty: "Dessinez un rectangle sur la grille pour obtenir un devis.",
      clearSelection: "Effacer la sélection",
      occupiedSelf: "Cette zone chevauche une parcelle que vous possédez déjà. Un terrain déjà frappé ne peut pas être racheté.",
      occupiedOther: "Cette zone chevauche un terrain détenu par un autre wallet. Dessinez sur une grille libre pour acheter.",
      occupiedGeneric: "Cette zone chevauche des parcelles existantes et n'est pas achetable. Dessinez sur une grille libre.",
      loading: "chargement du devis…",
    },
    errors: {
      tooWide: "Sélection trop large — une seule parcelle peut faire au plus 250 cellules de large. Dessinez une zone plus étroite.",
      tooHigh: "Sélection trop haute — une seule parcelle peut faire au plus 250 cellules de haut. Dessinez une zone plus basse.",
      areaExceeds: "Sélection trop grande — une seule parcelle peut couvrir au plus 10 000 slots (cellules){yours}. Dessinez une zone plus petite pour obtenir un devis.",
      areaExceedsYours: " — la vôtre fait {slots} slots",
      outOfBounds: "La sélection dépasse le bord de la Grid. Gardez-la entièrement dans le territoire 1000 × 1000.",
      zeroOrNegative: "La sélection est vide — dessinez un rectangle d'au moins une cellule de large.",
      vipCap: "Cette zone VIP dépasse le plafond commercial par parcelle. Dessinez une zone plus petite.",
      zoneCap: "Cette zone dépasse la capacité restante de sa zone. Dessinez une zone plus petite.",
      unexpected: "Erreur inattendue.",
    },
  },
  profile: {
    badge: { none: "Pas encore de badge" },
    cockpit: {
      title: "Aperçu",
      available: "Disponible",
      pendingFunding: "Financement en attente",
      ownedParcels: "Parcelles détenues",
      influence: "Influence",
      lifetimePts: "points cumulés",
      slots: "{n} slots",
      progressTo: "Progression vers {badge}",
      max: "max",
      referralCode: "Code de parrainage",
      qualified: "{n} qualifiés",
      pending: "{n} en attente",
    },
    myGrid: {
      loading: "Chargement de votre grille…",
      signIn: "Se connecter",
      unavailable: "Indisponible.",
      failedToLoad: "Échec du chargement",
      retry: "Réessayer",
      title: "Ma Grid",
      empty: "Vous ne possédez encore aucun terrain. Acquérez une parcelle pour commencer à gagner des récompenses de Voisinage et de l'Influence.",
      parcels: "Parcelles",
      totalSlots: "Slots au total",
      clusters: "Clusters",
      largestCluster: "Plus grand cluster",
      slots: "{n} slots",
      parcelMeta: "{w}×{h} · {area} slots",
      edgeNeighbors: "{n} voisins de bord",
    },
    influence: {
      title: "Score d'Influence",
      lifetimePrivate: "Points cumulés · privé",
      ptsTo: "{points} pts avant ",
      maxReached: "Badge maximal atteint",
      nextAt: "prochain à {threshold}",
      publicNote: "Les surfaces publiques n'affichent que votre badge — jamais votre score exact. Aucun classement.",
    },
    analytics: {
      title: "Analyses",
      retry: "Réessayer",
      loadError: "Impossible de charger vos analyses.",
      sections: { land: "Terrain et propriété", creator: "Créateur et parrainage", trends: "Tendances", rewards: "Récompenses et Influence" },
      kpi: {
        parcelsOwned: "Parcelles détenues",
        totalArea: "Surface totale",
        primaryAcq: "Acquisitions primaires",
        acqVolume: "Volume d'acquisition",
        ownedTerritories: "Territoires détenus",
        publishedTerritories: "Territoires publiés",
        createdArtworks: "Œuvres créées",
        qualifiedReferrals: "Parrainages qualifiés",
        influence: "Influence",
        rewardsAvailable: "Récompenses disponibles",
        totalRewards: "Récompenses totales",
        pendingFunding: "Financement en attente",
      },
      cells: "cellules",
      referralQ: "Qualifications de parrainage",
      inRange: "{n} sur la période",
      noReferrals: "Aucun parrainage qualifié sur cette période pour l'instant.",
      rewardsByType: "Récompenses par type",
      influenceGrowth: "Croissance de l'influence",
      noInfluence: "Aucune influence sur cette période pour l'instant.",
      notLiveTitle: "Les Récompenses et l'Influence ne sont pas encore actives.",
      notLiveBody: "Ces indicateurs se remplissent automatiquement dès que le programme The Grid Rewards est lancé. Rien n'est suivi ni estimé d'ici là.",
    },
    activity: {
      title: "Activité",
      markAll: "Tout marquer comme lu ({n})",
      filters: { all: "Tous", rewards: "Récompenses", land: "Terrain", artwork: "Œuvres", account: "Compte" },
      loadError: "Impossible de charger votre activité.",
      retry: "Réessayer",
      empty: "Aucune activité pour l'instant. Les événements importants de votre Grid apparaîtront ici.",
      loading: "Chargement…",
      loadMore: "Charger plus",
    },
    rewards: {
      loading: "Chargement des récompenses…",
      failedToLoad: "Impossible de charger les récompenses",
      auth: { note: "Connectez-vous pour voir votre solde de Récompenses et vos retraits.", cta: "Aller à la connexion" },
      unavailable: "Récompenses indisponibles.",
      retry: "Réessayer",
      errors: {
        belowMin: "Le montant est inférieur au minimum de 350 USDC.",
        exceedsAvailable: "Le montant dépasse votre solde Disponible.",
        invalidAmount: "Saisissez un montant valide.",
        unauthenticated: "Votre session a expiré — reconnectez-vous.",
        failed: "Le retrait a échoué.",
      },
      balance: {
        title: "Solde des Récompenses",
        available: "Disponible",
        pendingFunding: "Financement en attente",
        awaitingVault: "En attente du financement du Vault",
        withdrawalPending: "Retrait en attente",
        withdrawn: "Retiré",
        expired: "Expiré",
        expiryDeadline: "Date d'expiration",
        forfeits: "Le Disponible est perdu s'il n'est pas réclamé",
        noTimer: "Aucun minuteur actif",
      },
      withdraw: {
        title: "Retirer",
        disabledNote: "Il vous faut au moins {min} Disponible pour retirer. Disponible actuel : {available}.",
        amountLabel: "Montant (USDC)",
        gasLabel: "Estimation du gas réseau (USDC · facultatif)",
        allToggle: "Retirer tout le Disponible ({available})",
        serviceFee: "Frais de service · 2%",
        gasSeparate: "Gas (séparé)",
        estimatedNet: "Net estimé",
        minNote: "Minimum {min} brut. Des frais de service de 2% s'appliquent ; le gas réseau est facturé séparément et n'est qu'une estimation.",
        belowMin: "Sous le minimum de 350 USDC.",
        exceeds: "Dépasse votre solde Disponible.",
        submitting: "Envoi…",
        submit: "Retirer",
        all: "Tout retirer",
        success: "Retrait demandé — net {net} (frais {fee}, gas {gas}).",
      },
      payout: {
        title: "Besoin d'aide pour le règlement ?",
        body: "Avertissez l'équipe de traiter votre solde Disponible ({available}). Cela ne crée pas d'argent et ne contourne jamais l'ordre de financement.",
        reachTitle: "Atteignez {min} de profits Disponibles pour demander un paiement",
        sending: "Envoi…",
        cta: "Demander un paiement",
        minHint: "Atteignez {min} de profits Disponibles pour demander un paiement — vous avez {available}.",
        alreadyOpen: "Une demande de paiement est déjà ouverte.",
        sent: "Demande de paiement envoyée à l'équipe.",
        failed: "Impossible d'envoyer la demande.",
      },
      history: {
        title: "Historique des Récompenses",
        empty: "Aucune récompense pour l'instant. Les récompenses de Parrainage (7%) et de Voisinage (3%) apparaissent ici au fur et à mesure des acquisitions de terrain.",
        referralProv: "Parrainage · {pct}% de {gross}",
        neighborProvEdge: "Voisinage · {edge}/{total} de bordure d'un pool de {pct}%",
        neighborProvPool: "Voisinage · pool de {pct}%",
        funded: "Financé",
        pendingFunding: "Financement en attente",
        parcels: "parcelles {ids}",
      },
      withdrawals: {
        title: "Retraits",
        pending: "En attente",
        withdrawn: "Retiré",
        failed: "Échoué",
        net: "{net} net",
        gross: "brut {x}",
        fee: "frais {x}",
        gas: "gas {x}",
        failureReason: "· {reason} — rendu au Disponible pour réessayer",
      },
    },
  },
  dashboard: {
    publishability: { published: "Publié", publishable: "Publiable", notPublishable: "Non publiable" },
    territory: { fallback: "Territoire {ordinal}" },
    route: {
      title: "Mes territoires",
      subtitle: "Centre de commande du propriétaire · dérivé de votre propriété réelle sur la Grille",
      backToGrid: "Retour à la Grille",
      authTitle: "Authentification requise",
      authBody: "Connectez-vous avec votre portefeuille pour gérer vos territoires. Connecter un portefeuille ne suffit pas — une session signée est requise.",
      signIn: "Se connecter avec Ethereum",
      detailEmpty: "Sélectionnez un territoire pour voir son résumé de gestion.",
    },
    list: {
      resolving: "Résolution de vos territoires…",
      unavailableTitle: "Territoires indisponibles",
      unavailableBody: "Vos données de propriété complètes n'ont pas pu être résolues pour le moment. Rien de partiel n'est affiché. Veuillez réessayer sous peu.",
      emptyTitle: "Aucun territoire pour l'instant",
      emptyBody: "Vous ne possédez encore aucune propriété sur la Grille.",
      exploreGrid: "Explorer la Grille",
    },
    card: {
      area: "Superficie",
      gpCapacity: "Capacité GP",
      propertyUnits: "Unités de propriété",
      minNote: "Minimum 400 u² pour publier.",
      unreadTitle: "Un administrateur vous a envoyé un message de modération",
      unreadAria: "Message de modération non lu",
    },
    detail: {
      territoryArea: "Superficie du territoire",
      gpCapacity: "Capacité GP",
      propertyUnits: "Unités de propriété",
      underlyingUnitIds: "ID des unités sous-jacentes",
      location: "Emplacement (limites du monde)",
      minNote: "Ce territoire est en dessous du minimum de 400 u² requis pour publier.",
    },
    identity: {
      title: "Identité du territoire",
      rev: "rév {n}",
      authRequired: "Une session signée est requise pour gérer l'identité de ce territoire.",
      unavailable: "La gestion de l'identité est disponible uniquement pour un territoire légitimement possédé.",
      loading: "Résolution du territoire actuel…",
      namePlaceholder: "Nom d'affichage — nommez ce territoire",
      saved: "Enregistré.",
      saving: "Enregistrement…",
      saveName: "Enregistrer le nom",
      editCanvas: "Modifier le canevas →",
      saveErrors: {
        STALE_TERRITORY: "Votre territoire a changé depuis son chargement. Il a été actualisé — vérifiez et enregistrez à nouveau.",
        IDENTITY_VERSION_CONFLICT: "Cette identité a été mise à jour ailleurs. Elle a été actualisée — vérifiez et enregistrez à nouveau.",
        IDENTITY_RECONCILIATION_REQUIRED: "Ce territoire fusionne désormais avec un autre territoire nommé. Une réconciliation est requise avant d'enregistrer.",
        TERRITORY_NOT_PUBLISHABLE: "Ce territoire est en dessous du minimum de 400 u² requis pour publier.",
        TERRITORY_NOT_IDENTITY_ELIGIBLE: "La gestion de l'identité est disponible uniquement pour un territoire légitimement possédé.",
        DISPLAY_NAME_INVALID: "Ce nom n'est pas autorisé.",
        UNTRUSTED_ORIGIN: "Cette requête a été bloquée pour des raisons de sécurité. Veuillez recharger la page et réessayer.",
        CSRF_REQUIRED: "Votre jeton de sécurité était manquant. Veuillez recharger la page et réessayer.",
        CSRF_INVALID: "Votre jeton de sécurité a expiré. Veuillez recharger la page et réessayer.",
        CSRF_FETCH_FAILED: "Impossible de vérifier votre session. Veuillez recharger la page et réessayer.",
        UNAUTHENTICATED: "Votre session a expiré. Veuillez vous reconnecter pour enregistrer.",
        UNSUPPORTED_MEDIA_TYPE: "Une erreur s'est produite lors de l'envoi de votre requête. Veuillez recharger et réessayer.",
        SEED_NOT_OWNED: "Vous ne possédez plus ce territoire.",
      },
    },
    reconciliation: {
      intro: "Ce territoire fusionne désormais {n} territoires nommés en un seul. Choisissez quelle identité subsiste — les autres sont remplacées. Les noms ne sont pas fusionnés.",
      metaOne: "{n} unité · rév {rev}",
      metaOther: "{n} unités · rév {rev}",
      reconciling: "Réconciliation…",
      confirmSurvivor: "Confirmer le survivant",
      errors: {
        STALE_TERRITORY: "Votre territoire a changé depuis son chargement. Il a été actualisé — vérifiez et choisissez à nouveau.",
        IDENTITY_VERSION_CONFLICT: "L'une de ces identités a changé ailleurs. Elle a été actualisée — vérifiez et choisissez à nouveau.",
        RECONCILIATION_NOT_REQUIRED: "Ce territoire n'a plus besoin de réconciliation. Il a été actualisé.",
        SURVIVOR_NOT_CANDIDATE: "Cette identité n'est plus éligible. Elle a été actualisée.",
        TERRITORY_NOT_PUBLISHABLE: "Ce territoire est en dessous du minimum de 400 u² requis pour publier.",
        UNTRUSTED_ORIGIN: "Cette requête a été bloquée pour des raisons de sécurité. Veuillez recharger la page et réessayer.",
        CSRF_REQUIRED: "Votre jeton de sécurité était manquant. Veuillez recharger la page et réessayer.",
        CSRF_INVALID: "Votre jeton de sécurité a expiré. Veuillez recharger la page et réessayer.",
        CSRF_FETCH_FAILED: "Impossible de vérifier votre session. Veuillez recharger la page et réessayer.",
        UNAUTHENTICATED: "Votre session a expiré. Veuillez vous reconnecter.",
        TRANSACTION_UNAVAILABLE: "Le service est momentanément indisponible. Veuillez réessayer dans un instant.",
        SEED_NOT_OWNED: "Vous ne possédez plus ce territoire.",
      },
    },
    brandImage: {
      title: "Image de marque · LOD0",
      rev: "rév {n}",
      desc: "Image de carte optionnelle pour ce territoire (PNG · JPEG · WebP, ≤ 5 MiB, ≤ 2048px). Gratuit.",
      recommendedPrefix: "Recommandé pour votre territoire :",
      size: "{rw} × {rh} px",
      ratioNote: "(ratio {ratio}) — correspond à votre parcelle pour que le logo ne soit pas étiré.",
      previewAlt: "Image de marque du territoire",
      unsaved: "Aperçu non enregistré — appuyez sur Enregistrer pour le conserver.",
      empty: "Pas encore d'image de marque.",
      saved: "Enregistré.",
      removed: "Image de marque supprimée.",
      authRequired: "Une session signée est requise pour gérer l'image de marque.",
      chooseAria: "Choisir une image de marque",
      replace: "Remplacer…",
      select: "Sélectionner une image…",
      saving: "Enregistrement…",
      save: "Enregistrer",
      cancel: "Annuler",
      remove: "Supprimer",
      removeConfirm: "Supprimer l'image de marque ?",
      removing: "Suppression…",
      confirm: "Confirmer",
      keep: "Conserver",
    },
    publish: {
      publishing: "Publication…",
      republish: "Republier",
      published: "Publié",
      publish: "Publier",
      blockedSave: "Enregistrez votre brouillon avant de publier",
      stateLabels: {
        UNPUBLISHED: "Non publié",
        PUBLISHED_CURRENT: "Publié · actuel",
        DRAFT_CHANGED: "Publié · brouillon modifié",
        TERRITORY_CHANGED: "Publié · territoire modifié",
      },
    },
    media: {
      fileTooLarge: "Le fichier dépasse le maximum de 250 MiB.",
      title: "Bibliothèque de médias",
      subtitle: "Médias réutilisables de votre portefeuille · Image 10 · GIF 20 (10s) · PDF 25 · Vidéo 250 MiB · Bibliothèque 2 GiB",
      working: "En cours…",
      upload: "Téléverser",
      used: "{x} utilisés",
      free: "{free} libres sur {total}",
      loading: "Chargement de la bibliothèque…",
      empty: "Pas encore de médias. Téléversez une image, un GIF, un PDF ou une vidéo pour démarrer votre bibliothèque.",
      loadMore: "Charger plus",
      pages: "{n} pages",
      deleteAria: "Supprimer la ressource",
    },
  },
  artist: {
    utility: {
      loading: "Chargement de l'utilité de la parcelle…",
      referral: {
        title: "Parrainage",
        yourCode: "Votre code de parrainage",
        copyLink: "Copier le lien de parrainage",
        copied: "Copié",
        qualified: "Parrainages qualifiés",
        pending: "Parrainages en attente",
        empty: "Pas encore de parrainages. Partagez votre lien — un parrainage se qualifie dès que votre invité effectue sa première acquisition de terrain primaire.",
        preparing: "Votre code de parrainage est en cours de préparation à partir de la propriété canonique — revenez sous peu.",
        ownToUnlock: "Possédez au moins une parcelle pour débloquer votre code de parrainage.",
        wasReferred: "Vous avez été parrainé · statut",
      },
      parcel: {
        title: "Utilité de la parcelle",
        empty: "Vous ne possédez actuellement aucune parcelle. Acquérez du terrain pour débloquer l'utilité de la parcelle.",
        parcels: "Parcelles",
        totalArea: "Superficie totale",
        clusters: "Grappes",
        largestCluster: "Plus grande grappe",
        flagBasicIdentity: "Identité de base",
        flagPublishable: "Publiable",
        flagPublished: "Publié",
        flagHasNeighbors: "A des voisins",
        neighbors: "{n} voisins",
        cluster: "grappe {size} · {area} u²",
      },
      neighborhood: {
        title: "Voisinage",
        empty: "Possédez une parcelle pour voir votre voisinage.",
        edgeNeighbors: "Voisins de bordure",
        connectedClusters: "Grappes connectées",
        connectedClusterLabel: "Grappe connectée",
        clusterSummary: "{size} parcelles · {area} u²",
        openParcel: "Parcelle #{id} — {n} voisin(s) de bordure",
        loading: "Chargement…",
        noEdgeNeighbors: "Aucun voisin de bordure.",
        yours: "le vôtre",
        published: "publié",
      },
    },
    profile: {
      loading: "Chargement du profil…",
      connectWallet: "Connectez votre portefeuille",
      connectBody: "Connectez-vous pour voir votre profil d'artiste, votre éligibilité et votre capacité créative.",
      goToSignIn: "Aller à la connexion",
      unavailable: "Profil indisponible.",
      retry: "Réessayer",
      loadFail: "Échec du chargement du profil",
      moderationLabel: "Modération",
      moderationMsg: "Message de l'admin",
      moderationAria: "Vous avez un message d'un administrateur — ouvrez Mes territoires pour le lire",
      tabs: { overview: "Aperçu", rewards: "Récompenses", activity: "Activité", analytics: "Analyses", grid: "Ma Grille" },
      roleTitle: "Rôle",
      roleArtist: "Vous êtes un Artiste — une identité permanente obtenue grâce à l'éligibilité foncière et à l'ancienneté.",
      roleNeo: "Vous êtes un NéoArtiste — une identité permanente avec un accès accéléré à la création Artiste une fois éligible. Mêmes capacités créatives qu'un Artiste.",
      roleCreator: "Vous possédez actuellement du terrain, vous pouvez donc utiliser les outils créatifs. Créateur est un état actuel — il suit votre propriété foncière et n'est pas un badge permanent.",
      roleUser: "Vous ne possédez pas de terrain actuellement et n'avez pas débloqué d'identité artistique permanente. Acquérez du terrain pour devenir Créateur, ou atteignez 50 % d'éligibilité pour la voie NéoArtiste.",
      statusCurrentState: "· état actuel",
      eligTitle: "Éligibilité Artiste",
      neoThreshold: "Seuil NéoArtiste · 50%",
      artistThreshold: "Seuil Artiste · 100%",
      eligUnits: "{units} / {total} unités d'éligibilité",
      tierSlots: "{slots} emplacements · {units}u",
      tenureTitle: "Ancienneté Artiste · 100 jours",
      tenureBlock: " (bloc {block})",
      tenurePending: "Première acquisition de terrain enregistrée on-chain{block}. La date exacte est en cours de résolution à partir des données canoniques de la chaîne — l'ancienneté s'affichera une fois disponible. Aucune date n'est affichée tant qu'elle ne peut être prouvée.",
      firstAcq: "Première acquisition : {date}",
      daysLine: "{days} / {min} jours",
      reqMet: "exigence satisfaite",
      daysRemaining: "{n} jours restants",
      ccTitle: "Capacité créative",
      ccTotal: "Total",
      ccUsed: "Utilisée",
      ccAvailable: "Disponible",
      ccNote: "La capacité provient du terrain que vous possédez et reste liée à l'historique de chaque parcelle.",
      neoTitle: "Voie NéoArtiste",
      neoHavePermanent: "Vous détenez déjà une identité permanente {status}.",
      neoNeedElig: "Atteignez 50% d'éligibilité Artiste pour débloquer la voie NéoArtiste accélérée (actuellement {pct}%).",
      neoEligibleBody: "Vous êtes éligible à la voie NéoArtiste accélérée (frais uniques, en USDC ou USDT). NéoArtiste n'accorde aucune capacité créative supplémentaire.",
      neoFee: "Frais : {fee} · {currencies}",
      neoFeeNotConfigured: "Frais pas encore configurés — aucun prix n'est affiché tant que The Grid ne l'a pas fixé.",
      neoPaymentPending: "Intégration de paiement en attente",
      collectionTitle: "Collection",
      collCreatedBy: "Créé par",
      collOwned: "Possédé",
      collCreatedCount: "{n} créés",
      collCreatedEmpty: "Pas encore d'œuvres figées.",
      collOwnedEmpty: "Aucune œuvre actuellement possédée.",
      editTitle: "Modifier le profil",
      editDisplayName: "Nom d'affichage",
      editBio: "Bio",
      editAvatar: "URL de l'avatar (https/ipfs)",
      editSaving: "Enregistrement…",
      editSave: "Enregistrer",
      editSaved: "Enregistré",
      editSaveFail: "Échec de l'enregistrement",
    },
  },
  social: {
    follow: { follow: "Suivre", following: "Suivi", actionFailed: "Échec de l'action" },
    report: {
      moreOptions: "Plus d'options",
      reason: "Motif",
      report: "Signaler",
      blockUser: "Bloquer l'utilisateur",
      reported: "Signalé. Merci.",
      reportFailed: "Échec du signalement",
      blocked: "Bloqué.",
      blockFailed: "Échec du blocage",
      reasons: {
        spam: "Spam", abuse: "Abus", harassment: "Harcèlement", hate: "Haine",
        nsfw: "NSFW", scam: "Arnaque", impersonation: "Usurpation d'identité", other: "Autre",
      },
    },
    bar: {
      save: "Enregistrer", saved: "Enregistré", comments: "Commentaires",
      likeError: "Impossible de mettre à jour le j'aime", saveError: "Impossible de mettre à jour l'enregistrement",
    },
    comment: {
      post: "Publier", postError: "Impossible de publier", removed: "[supprimé]", you: "vous",
      reply: "Répondre", hide: "Masquer", viewOne: "Voir {n} réponse", viewMany: "Voir {n} réponses",
      delete: "Supprimer", loadingReplies: "Chargement des réponses…", replyPlaceholder: "Écrivez une réponse…",
      heading: "Commentaires", topPlaceholder: "Partagez vos réflexions sur ce Territoire…",
      signin: "Connectez-vous pour rejoindre la conversation →", loading: "Chargement des commentaires…",
      empty: "Pas encore de commentaires. Soyez le premier.", loadingMore: "Chargement…", loadMore: "Charger plus",
    },
    creator: {
      signinPre: "Connectez-vous", signinPost: "pour voir votre profil.",
      none: "Aucun créateur spécifié.", loading: "Chargement du profil…", unavailable: "Profil indisponible.",
      followers: "Abonnés", following: "Abonnements", likes: "J'aime", comments: "Commentaires",
      territories: "Territoires", published: "Publiés",
      gotoFeed: "Aller à votre fil →", recentActivity: "Activité récente",
    },
    feed: {
      title: "Social", yourProfile: "Votre profil →",
      following: "Abonnements", discover: "Découvrir",
      followingSignin: "Connectez-vous pour voir les créateurs que vous suivez →",
      loading: "Chargement du fil…", unavailable: "Fil indisponible.",
      emptyFollowing: "Votre fil est calme. Suivez des créateurs pour voir ici leurs derniers Territoires et œuvres.",
      emptyDiscover: "Rien à découvrir pour l'instant — les nouveaux Territoires publiés apparaissent ici.",
      loadingMore: "Chargement…", loadMore: "Charger plus",
      titlePublication: "A publié un Territoire",
      titleArtworkNamed: "A publié l'œuvre « {title} »",
      titleArtwork: "A publié une œuvre",
      titleAcquiredParcel: "A acquis la parcelle #{id}",
      titleAcquired: "A acquis un nouveau Territoire",
    },
    handle: {
      heading: "Votre profil social", handleLabel: "Pseudo", handlePlaceholder: "votre_pseudo",
      save: "Enregistrer", handleSaved: "Pseudo enregistré.", handleError: "Impossible d'enregistrer le pseudo",
      handleHint: "3 à 24 caractères · lettres, chiffres, underscore · modifiable tous les 30 jours.",
      linksLabel: "Liens", linkLabelPlaceholder: "Libellé", addLink: "+ Ajouter un lien",
      saveLinks: "Enregistrer les liens", linksSaved: "Liens enregistrés.", linksError: "Impossible d'enregistrer les liens",
    },
  },
  legal: {
    navAria: "Navigation des documents juridiques",
  },
  editor: {
    pixel: {
      title: "Pixel Studio",
      deleteTitle: "Supprimer ce pixel art",
      delete: "Supprimer",
      closeTitle: "Fermer le studio",
      startingPoint: "Point de départ",
      startingPointBody: "Nous avons transformé votre image en pixels pour vous donner une base. Maintenant, faites-la vôtre…",
      pencil: "Crayon", eraser: "Gomme", fill: "Remplir", pick: "Pipette",
      undo: "Annuler", redo: "Rétablir", clear: "Effacer", grid: "Grille",
      importOpen: "Importer une image → Pixel",
      brush: "Pinceau", zoom: "Zoom",
      zoomOutTitle: "Zoom − (cellules plus petites)", zoomInTitle: "Zoom + (cellules plus grandes)",
      cellsPainted: "Cellules peintes :",
      paletteActive: "Palette active", paletteActiveNote: "· image importée / dessin",
      add: "+ Ajouter", recent: "Récents",
      paletteBase: "Palette de base", paletteBaseNote: "· couleurs standard THE GRID",
      saving: "Enregistrement…", saveArtworkCanvas: "Enregistrer l'œuvre + le canevas",
      unsaved: "Non enregistré", saved: "Enregistré", close: "Fermer",
      pixelizeTitle: "Pixeliser l'image", cancel: "Annuler",
      resultingGrid: "Grille résultante :", colorsLabel: "couleurs :",
      resolution: "Résolution (grand côté)", fitMode: "Ajustement",
      fitContain: "Contenir", fitCover: "Remplir", maxColors: "Couleurs max",
      dithering: "Tramage", on: "ON", off: "OFF",
      alphaThreshold: "Seuil de transparence ({v})", useAsBase: "Utiliser comme base",
      unsavedTitle: "Modifications non enregistrées",
      unsavedBody: "Vous avez des modifications de pixel art non encore enregistrées sur le canevas. Que voulez-vous faire ?",
      saveAndClose: "Enregistrer et fermer", discardClose: "Fermer sans enregistrer",
    },
    voxel: {
      invalid: "Route de l'éditeur 3D non valide.",
      authRequired: "Connectez-vous avec votre portefeuille pour modifier cette scène 3D.",
      signIn: "Se connecter →",
      loading: "Chargement de l'éditeur 3D…",
      back: "Retour",
      empty: "Aucun territoire.",
      failedToLoad: "Échec du chargement",
      noFootprint: "Ce territoire n'a aucune parcelle possédée sur laquelle construire.",
      flashCreateFirst: "Créez ou sélectionnez d'abord une Œuvre",
      flashOutside: "En dehors de votre emprise possédée",
      flashCleared: "Pièce active vidée",
      flashNoImage: "Impossible de lire l'image",
      flashBaseInside: "La base doit rester à l'intérieur de votre territoire",
      canvas: "Canevas",
      add: "Ajouter", remove: "Supprimer", cancelBase: "Annuler la base",
      currentColorTitle: "Couleur actuelle (changez-la dans la barre latérale)",
      undo: "Annuler", undoTitle: "Annuler (Ctrl+Z)",
      redo: "Rétablir", redoTitle: "Rétablir (Ctrl+Maj+Z)",
      clear: "Effacer", clearTitle: "Vider la pièce active",
      resetView: "Réinitialiser la vue",
      saving: "Enregistrement…", saved: "Enregistré", save: "Enregistrer",
      saveTitle: "Enregistrer le brouillon 3D", saveOverTitle: "Capacité dépassée — supprimez des voxels pour enregistrer",
      publishTitle: "La publication se fait sur le Canevas — elle inclut automatiquement cette scène 3D enregistrée",
      publishLink: "Publier sur le Canevas →",
      reload: "Recharger",
      color: "Couleur", customColorTitle: "Couleur personnalisée",
      workArts: "Œuvres", newBase: "+ Nouvelle base",
      budgetLabel: "Budget :", cellsUnit: "cellules",
      noWorkArts: "Pas encore d'œuvres.",
      activePiece: "Pièce active", rotate: "Pivoter", rotationHint: "Remettez la rotation à 0° pour modifier les voxels.",
      imageToVoxel: "Image → Voxel", depth: "Profondeur", converting: "Conversion…", chooseImage: "Choisir une image…",
      conversionHelp: "La conversion vous aide à démarrer avec une simple extrusion. Vous pouvez affiner le résultat voxel par voxel.",
      fallback: "L'édition 3D nécessite WebGL, que cet appareil n'a pas pu démarrer. Votre brouillon 3D est en sécurité — essayez un navigateur de bureau avec accélération matérielle.",
      defineTitle: "Définir la base — glissez sur la grille",
      defineBody: "Glissez un rectangle sur le sol pour dimensionner votre pièce, puis relâchez pour la créer.",
      sizeLabel: "Taille", tooBig: "· trop grand",
      noCapacityTitle: "Aucune capacité 3D libre",
      noCapacityBody: "Vous ne pouvez pas encore placer de blocs sur ce territoire. Le cadeau 3D gratuit (10 000+ voxels) n'est accordé qu'au portefeuille qui a acheté une parcelle lors de la vente primaire et la possède toujours. Les parcelles réclamées/mintées ou achetées d'occasion n'en reçoivent aucun.",
      hintNewBase: "Cliquez sur « + Nouvelle base », puis glissez sur la grille pour dimensionner votre pièce.",
      howToBuild: "Comment construire",
      step1: "Cliquez + Nouvelle base, puis glissez sur la grille pour dimensionner la pièce",
      step2: "Clic gauche sur le sol / la face d'un bloc pour placer des blocs",
      step3: "Clic droit sur un bloc pour le supprimer",
      mouse: "Souris", showControls: "Afficher les contrôles et comment construire", hide: "Masquer",
      leftClick: "Clic gauche", rightClick: "Clic droit", ctrlLeft: "Ctrl + gauche", ctrlRight: "Ctrl + droit", scroll: "Molette",
      drawBase: "Dessiner la base", placeBlock: "Placer un bloc", removeBlock: "Supprimer le bloc", orbit: "Orbiter autour du clic", pan: "Déplacer la vue", zoomInOut: "Zoom avant / arrière",
      capacity3d: "Capacité 3D", overCapacity: "Capacité dépassée",
    },
    templates: {
      title: "Modèles Canvas",
      curatedStarts: "6 points de départ sélectionnés",
      close: "Fermer les modèles",
      apply: "Appliquer le modèle",
      previewAria: "Aperçu de la mise en page de {name}",
      replaceTitle: "Remplacer le brouillon actuel ?",
      replaceBody: "Appliquer {name} remplacera le contenu de votre brouillon actuel ({count} {unit}). Votre territoire publié n'est pas affecté tant que vous n'avez pas enregistré et publié. Cette action ne peut être annulée qu'avec Annuler.",
      elementOne: "élément",
      elementOther: "éléments",
      cancel: "Annuler",
      replaceDraft: "Remplacer le brouillon",
      catalog: {
        creator: { name: "Créateur / Marque personnelle", useCase: "Identité personnelle", tagline: "Une identité audacieuse et centrée — nom, rôle, bio, réseaux et un unique appel à l'action.", tag0: "Identité", tag1: "Bio", tag2: "Réseaux" },
        retail: { name: "Commerce / Vitrine", useCase: "Petite marque & boutique", tagline: "Un en-tête de marque sur une étagère de produits rythmée, avec une action claire visiter-la-boutique.", tag0: "Produits", tag1: "Étagère", tag2: "Lien boutique" },
        gallery: { name: "Artiste / Galerie", useCase: "L'œuvre d'abord", tagline: "Un cadre dominant unique avec une déclaration sobre — l'œuvre guide, les mots suivent.", tag0: "Œuvre", tag1: "Déclaration", tag2: "Minimal" },
        music: { name: "Musique / Événement", useCase: "Sortie & événement live", tagline: "Titre énergique, date et lieu, et une action directe billets / streaming.", tag0: "Événement", tag1: "Date", tag2: "Billets" },
        product: { name: "Produit / Lancement tech", useCase: "Landing de lancement", tagline: "Une page de lancement moderne — titre, ligne de soutien, trois blocs de fonctionnalités et un CTA.", tag0: "Lancement", tag1: "Fonctionnalités", tag2: "CTA" },
        minimal: { name: "Luxe minimal", useCase: "Minimalisme haut de gamme", tagline: "Typographie sobre et espace généreux — un mot, une ligne, une action.", tag0: "Luxe", tag1: "Espace blanc", tag2: "Sobriété" },
      },
    },
    error: {
      title: "Le canevas a rencontré une erreur",
      body: "L'éditeur s'est rétabli au lieu de devenir noir. Les modifications non enregistrées depuis votre dernier enregistrement n'ont pas été écrites sur le serveur. Essayez d'ignorer pour continuer, ou rechargez l'éditeur pour un état propre.",
      dismiss: "Ignorer",
      reload: "Recharger l'éditeur",
    },
    canvas: {
      invalid: "Route de canevas invalide.",
      authRequired: "Connecte-toi avec ton wallet pour modifier ce Canevas.",
      signIn: "Se connecter →",
      loading: "Chargement du canevas…",
      empty: "Aucun canevas.",
      failedToLoad: "Échec du chargement",
      back: "Retour",
      close: "Fermer",
      cancel: "Annuler",
      exitConfirm: "Vous avez des modifications non enregistrées. Quitter sans enregistrer ?",
      flash: {
        saved3d: "Position 3D enregistrée",
        territoryChanged3d: "Territoire modifié — recharge l'éditeur",
        error3d: "3D : {code}",
        rotateOutside: "La rotation sortirait du territoire",
      },
      save: {
        territoryChanged: "Territoire modifié — recharger l'éditeur",
      },
      toolbar: {
        territories: "Territoires",
        undo: "Annuler",
        redo: "Rétablir",
        fit: "Ajuster le territoire",
        templates: "Modèles",
        templatesTitle: "Partir d'un modèle premium",
        templatesDisabledTitle: "Les modèles nécessitent un territoire possédé",
        mediaLibrary: "Bibliothèque média",
        mediaLibraryTitle: "Bibliothèque média — importer & parcourir sans quitter l'éditeur",
        artistProfile: "Profil artiste",
        artistProfileTitle: "Ouvrir ton profil artiste",
        editor3d: "Éditeur 3D",
        editor3dTitle: "Ouvrir l'éditeur voxel 3D pour ce territoire",
        save: "Enregistrer",
        saving: "Enregistrement…",
        saveTitle: "Enregistrer le canevas",
        saveOverTitle: "Capacité dépassée — réduis l'usage de GP pour enregistrer",
        freeze: "Figer",
        freezeTitle: "Figer cette œuvre Pixel Studio",
        freezeBlockedTitle: "Enregistre le canevas avant de figer",
      },
      tools: {
        pixelArt: "Pixel Art",
        new: "NOUVEAU",
        text: "Texte",
        interactive: "Interactif",
        media: "Média",
        shapes: "Formes",
      },
      pixelDraw: {
        banner: "Dessine un rectangle sur ton territoire",
      },
      pixelSize: {
        title: "Nouvelle Pixel Art",
        body: "Choisis une taille de canevas — 1 cellule = 1 parcelle. Les cellules vides restent transparentes.",
        custom: "Personnalisé — dessine sur le territoire",
      },
      mobileReadonly: "Lecture / inspection seule sur cet appareil. L'édition nécessite un écran plus grand.",
      gp: {
        used: "Utilisés",
        available: "Disponibles {n} GP",
        overCapacity: "Capacité dépassée",
      },
      status: {
        saved: "Enregistré",
        unsavedChanges: "Modifications non enregistrées",
        saving: "Enregistrement…",
        error: "Erreur",
        showError: "Afficher l'erreur complète",
      },
      inspector: {
        territoryTiers: "Niveaux du territoire",
        globalStyle: "Style global",
        background: "Arrière-plan",
        bgColor: "Couleur de fond",
        perimeter: "Périmètre",
        perimeterGlow: "Lueur du périmètre",
        opacity: "Opacité {v}%",
        glow: "Lueur",
        glowColor: "Couleur de lueur",
        text: "Texte",
        label: "Libellé",
        subtitleOptional: "Sous-titre (optionnel)",
        urlHttps: "URL (https)",
        previewOpen: "Aperçu / Ouvrir ↗",
        fill: "Remplissage",
        lineColor: "Couleur de ligne",
        fit: "Ajustement",
        pixelArt: "Pixel Art",
        editPixels: "Modifier les pixels",
        title: "Titre",
        bgPreview: "Aperçu de l'arrière-plan",
        changeImage: "Changer l'image",
        addImage: "+ Ajouter une image",
        selectedGp: "GP {used} / {max} max",
      },
      controls: {
        preset: "Préréglage",
        layout: "Disposition",
        direction: "Direction",
        columns: "Colonnes {v}",
        align: "Alignement",
        gap: "Espacement {v}",
        iconSize: "Taille d'icône {v}",
        labelOptional: "Libellé (optionnel)",
        labelColor: "Couleur du libellé",
        brandColors: "Couleurs de marque",
        iconColor: "Couleur d'icône",
        backgroundChip: "Puce d'arrière-plan",
        bgColor: "Couleur de fond",
        border: "Bordure",
        borderColor: "Couleur de bordure",
        radius: "Rayon",
        padding: "Marge interne {v}",
        hover: "Survol",
        font: "Police",
        color: "Couleur",
        textSize: "Taille du texte {v}",
        style: "Style",
        subtitleColor: "Couleur du sous-titre",
        underline: "Souligner le libellé",
        icon: "Icône",
      },
      social: {
        links: "Liens ({n}/5)",
        removeLink: "Supprimer le lien",
        addLink: "+ Ajouter un lien",
        showLabels: "Afficher les libellés",
        labelFont: "Police du libellé",
        labelSize: "Taille du libellé {v}",
      },
      contacts: {
        heading: "Contacts ({n}/10)",
        removeContact: "Supprimer le contact",
        addContact: "+ Ajouter un contact",
        show: "Afficher",
        value: "Valeur",
        dataColor: "Couleur des données",
        kind: { EMAIL: "E-mail", PHONE: "Téléphone", WHATSAPP: "WhatsApp", WEBSITE: "Site web", MAP: "Adresse" },
        placeholder: { EMAIL: "nom@exemple.fr", PHONE: "+33 1 00 00 12 34", WHATSAPP: "+33 1 00 00 12 34", WEBSITE: "https://exemple.fr", MAP: "1 rue de la Paix, Ville" },
      },
      layers: {
        title: "Calques",
        empty: "Aucun module pour l'instant.",
        front: "Premier plan",
        forward: "Avancer",
        backward: "Reculer",
        back: "Arrière-plan",
        hide: "Masquer",
        lock: "Verrouiller",
      },
      mediaPanel: {
        title: "Bibliothèque média",
        working: "En cours…",
        upload: "Importer",
        hint: "Image 10 · GIF 20 (10s) · PDF 25 · Vidéo 250 Mio · Bibliothèque 2 Gio. Les imports apparaissent instantanément dans les sélecteurs Ajouter un média.",
        loading: "Chargement de la bibliothèque…",
        empty: "Aucun média pour l'instant. Importe une image, un GIF, un PDF ou une vidéo pour démarrer ta bibliothèque.",
        loadMore: "Charger plus",
        deleteAria: "Supprimer l'asset",
      },
      mediaPicker: {
        choose: "Choisir {kind} dans la bibliothèque média",
        empty: "Aucun asset {kind}. Importe d'abord dans la bibliothèque média du tableau de bord.",
      },
      mediaKind: { image: "image", gif: "GIF", video: "vidéo", pdf: "PDF" },
      errorModal: {
        saveFailed: "Échec de l'enregistrement",
        gotIt: "Compris",
      },
      tier: { STANDARD: "Standard", PREMIUM: "Premium", VIP_CORE: "VIP Core", RESERVE: "Réserve", GRID_ZERO: "GRID ZERO" },
      enum: {
        module: { TITLE: "Titre", TAGLINE: "Slogan", DESCRIPTION: "Description", LINK: "Lien", SOCIAL: "Social", CONTACTS: "Contacts", IMAGE: "Image", GIF: "GIF", VIDEO: "Vidéo", PDF: "PDF", PIXEL_ART: "Pixel Art", SHAPE: "Forme" },
        shape: { RECTANGLE: "Rectangle", ROUNDED_RECTANGLE: "Rectangle arrondi", ELLIPSE: "Ellipse", LINE: "Ligne" },
        bgMode: { NEUTRAL: "Neutre", SOLID_COLOR: "Couleur unie" },
        glow: { OFF: "Désactivé", LOW: "Faible", MEDIUM: "Moyen", HIGH: "Élevé" },
        direction: { HORIZONTAL: "Horizontal", VERTICAL: "Vertical", GRID: "Grille" },
        align: { START: "Début", CENTER: "Centre", END: "Fin" },
        textAlign: { LEFT: "Gauche", CENTER: "Centre", RIGHT: "Droite", JUSTIFY: "Justifié" },
        radius: { SQUARE: "Carré", SLIGHT: "Léger", ROUNDED: "Arrondi", PILL: "Pilule" },
        hover: { NONE: "Aucun", LIFT: "Élévation", GLOW: "Lueur", SCALE: "Échelle" },
        fit: { COVER: "Remplir", CONTAIN: "Ajuster" },
        contactsDisplay: { ICON: "Icône", ICON_LABEL: "Icône + libellé", ICON_DATA: "Icône + données", BUTTON: "Bouton" },
        linkIcon: { NONE: "Aucune", ARROW: "Flèche", FAVICON: "Favicon" },
        socialPreset: { ICONS_ONLY: "Icônes seules", MINIMAL: "Minimal", BUTTONS: "Boutons", CARDS: "Cartes" },
        contactsPreset: { ICONS_ONLY: "Icônes seules", BUSINESS: "Professionnel", MINIMAL: "Minimal" },
        linkPreset: { TEXT: "Texte", BUTTON: "Bouton", OUTLINE: "Contour", CARD: "Carte" },
      },
    },
  },
};

export default fr;
